// ==== CORE: Firebase/auth/nav + shared helpers + Pigeon List page (incl. Add/Edit Pigeon modal, Pigeon Pedigree page) ====
// Split from the original single tempdashboard.js (originally lines 2-3098).
// Loaded as a plain classic <script defer> alongside the other tempdashboard-*.js files --
// they all share one global scope (like separate parts of one big file), so functions and
// top-level let/const declared in any of these files are visible to all the others, as long
// as the <script> tags stay in the same order every one of these files was split in.

  // Strip Shopify's ?contact_posted=true#help-contact-form return flag from
  // the visible URL as early as possible -- doing this later (after auth/
  // Firestore have loaded) left it visible for a couple of seconds. This
  // needs nothing but the URL itself, so it can run before Firebase even
  // initializes. Remembered here so the actual banner/tab-switch (which
  // DOES need the dashboard to be ready) can still happen further down.
  const justSubmittedContactForm = new URLSearchParams(window.location.search).get('contact_posted') === 'true';
  if (justSubmittedContactForm) history.replaceState(null, '', window.location.pathname);

  // "Invite the club" join link -- ?joinFlight=<groupId>. Left in the URL
  // (unlike contact_posted above) since this can be someone's actual entry
  // point to the whole dashboard, not just a return trip -- keeping it
  // makes the link itself re-openable/bookmarkable to the same flight.
  const joinFlightGroupId = new URLSearchParams(window.location.search).get('joinFlight');

  const firebaseConfig = {
    apiKey: "AIzaSyBix0EICXEr3W6VqxrtWzXrKZnXKXOOYfc",
    projectId: "flightpathlive-24331",
    storageBucket: "flightpathlive-24331.firebasestorage.app",
    messagingSenderId: "240948118846"
  };
  if (localStorage.getItem('fpl-dark-mode') === '1') document.body.classList.add('dark-mode');
  firebase.initializeApp(firebaseConfig);
  const auth = firebase.auth();
  const db = firebase.firestore();

  // Wires up a section's info banner(s): dismissing any one of them hides
  // the whole group and leaves a small restore icon behind instead of
  // losing the explanation for good -- clicking that icon brings them all
  // back. Shared by every section's banner so each one doesn't reinvent
  // the same dismiss/localStorage plumbing.
  function setupDismissableBanners(bannerIds, restoreBtnId, storageKey) {
    const banners = bannerIds.map(id => document.getElementById(id)).filter(Boolean);
    const restoreBtn = restoreBtnId ? document.getElementById(restoreBtnId) : null;
    if (!banners.length) return;
    function setDismissed(dismissed) {
      banners.forEach(b => { b.style.display = dismissed ? 'none' : ''; });
      if (restoreBtn) restoreBtn.style.display = dismissed ? '' : 'none';
      try { localStorage.setItem(storageKey, dismissed ? '1' : '0'); } catch (e) {}
    }
    setDismissed(localStorage.getItem(storageKey) === '1');
    banners.forEach(b => {
      const closeBtn = b.querySelector('.db-notice-close');
      if (closeBtn) closeBtn.onclick = () => setDismissed(true);
    });
    if (restoreBtn) restoreBtn.onclick = () => setDismissed(false);
  }

  // Leaflet (+ polylineDecorator) and the Firebase Storage SDK are both
  // heavy and only needed some of the time -- Leaflet only once someone
  // opens New Track or Group Tracking, Storage only when a pigeon photo is
  // actually being uploaded. Loading them on demand instead of as static
  // <script> tags on every page load is what cuts the page's load time
  // down. Both loaders are promise-cached so opening a second tab or
  // saving a second photo doesn't re-fetch or re-run anything.
  const _scriptPromises = {};
  function loadScriptOnce(src) {
    if (!_scriptPromises[src]) {
      _scriptPromises[src] = new Promise((resolve, reject) => {
        const el = document.createElement('script');
        el.src = src;
        el.onload = () => resolve();
        el.onerror = () => reject(new Error('Failed to load ' + src));
        document.head.appendChild(el);
      });
    }
    return _scriptPromises[src];
  }
  function loadStyleOnce(href) {
    if (!_scriptPromises[href]) {
      _scriptPromises[href] = new Promise((resolve) => {
        const el = document.createElement('link');
        el.rel = 'stylesheet';
        el.href = href;
        el.onload = () => resolve();
        document.head.appendChild(el);
      });
    }
    return _scriptPromises[href];
  }

  let leafletReadyPromise = null;
  function ensureLeafletLoaded() {
    if (!leafletReadyPromise) {
      leafletReadyPromise = Promise.all([
        loadStyleOnce('https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.css'),
        loadScriptOnce('https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.js')
          .then(() => loadScriptOnce('https://cdn.jsdelivr.net/npm/leaflet-polylinedecorator@1.6.0/dist/leaflet.polylineDecorator.js'))
      ]);
    }
    return leafletReadyPromise;
  }

  let storage = null;
  let storageReadyPromise = null;
  function getStorage() {
    if (!storageReadyPromise) {
      storageReadyPromise = loadScriptOnce('https://cdnjs.cloudflare.com/ajax/libs/firebase/10.12.2/firebase-storage-compat.js')
        .then(() => { storage = firebase.storage(); return storage; });
    }
    return storageReadyPromise;
  }

  // Super-user login -- sees and can edit/delete every account's pigeons,
  // not just its own. Checked against the real signed-in Firebase user's
  // email, so it can't be spoofed by editing this page's code (someone
  // would need this exact account's real password to ever satisfy it).
  // The actual enforcement for edit/delete lives in firestore.rules, not
  // here -- this only controls what the UI shows/queries. Deliberately NOT
  // a hardcoded email list anymore -- that was readable by anyone via
  // "View page source" or just fetching this file directly (client-side
  // JS is never secret). checkIsAdmin() below asks the server instead,
  // which checks the caller's verified ID token against its own separate,
  // server-only admin list.
  let isAdmin = false;

  async function checkIsAdmin(user) {
    try {
      const idToken = await user.getIdToken();
      const resp = await fetch('https://us-central1-flightpathlive-24331.cloudfunctions.net/checkAdmin', {
        headers: { 'Authorization': 'Bearer ' + idToken }
      }).then(r => r.json());
      return !!(resp.ok && resp.isAdmin);
    } catch (e) {
      return false; // fail closed -- never show admin UI on a failed check
    }
  }
  let myTrackerLimit = null; // null = not loaded yet (or admin, who has no limit) -- see getMyTrackerLimit fetch below

  let currentOrg = null;

  function slugify(name) {
    return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }

  async function ensureOrganization(user) {
    const existing = await db.collection('organizations').where('ownerUid', '==', user.uid).limit(1).get();
    if (!existing.empty) return existing.docs[0].data();
    const name = user.email.split('@')[0] + "'s account";
    const slug = slugify(name);
    const orgData = { name, slug, ownerUid: user.uid, ownerEmail: user.email, createdAt: firebase.firestore.FieldValue.serverTimestamp() };
    await db.collection('organizations').doc().set(orgData);
    return orgData;
  }

  // The name shown next to the topbar avatar (and, via that element, in Recent
  // Activities' "Added By" and the Home greeting's fallback) is the Name the
  // user saved in Settings (currentOrg.displayName). Until they save one, it
  // falls back to the capitalized email prefix, same as before.
  function applyAccountDisplayName() {
    const user = auth.currentUser;
    const emailName = ((user && user.email) || 'User').split('@')[0];
    const saved = ((currentOrg && currentOrg.displayName) || '').trim();
    // Capitalize the first letter of each word for display only (a Name saved
    // as "harshad ghori" shows as "Harshad Ghori"); the rest of each word is
    // left as typed, and the saved value itself is never changed.
    const capitalized = saved.replace(/(^|\s)(\S)/g, (m, sp, ch) => sp + ch.toUpperCase());
    const name = capitalized || (emailName.charAt(0).toUpperCase() + emailName.slice(1));
    document.getElementById('db-avatar-letter').textContent = name.charAt(0).toUpperCase();
    document.getElementById('db-avatar-name').textContent = name;
    renderSettingsPhotos();
  }

  auth.onAuthStateChanged(async (user) => {
    if (!user) {
      window.location.href = '/pages/live-tracking';
      return;
    }
    try {
      currentOrg = await ensureOrganization(user);
    } catch (e) {
      // Happens if this login's account record is missing (e.g. manually
      // removed) while the login itself still works -- rather than hang on
      // a blank/stuck "Loading..." forever, say so clearly and offer a way
      // out instead of leaving the user stranded.
      document.getElementById('db-loading-msg').innerHTML =
        '<p style="color:#c0392b;">We couldn\'t find your account details. ' +
        'Please contact support, or <a href="#" id="db-loading-signout">sign out</a> and try again.</p>';
      document.getElementById('db-loading-signout').onclick = (ev) => {
        ev.preventDefault();
        auth.signOut();
      };
      return;
    }
    isAdmin = await checkIsAdmin(user);
    document.getElementById('db-org-name').textContent = currentOrg.name + (isAdmin ? ' (admin)' : '');
    document.getElementById('db-pigeon-search').placeholder = isAdmin
      ? 'Search by pigeon name, ring number, tracker tag or account...'
      : 'Search by pigeon name, ring number or tracker tag...';
    document.getElementById('trk-search').placeholder = isAdmin
      ? 'Search by tracker name, ID or account...'
      : 'Search by tracker name or ID...';
    document.getElementById('grp-search').placeholder = isAdmin
      ? 'Search by group name, tracker ID or account...'
      : 'Search by group name...';
    applyAccountDisplayName();
    document.getElementById('db-avatar-role').textContent = isAdmin ? 'Admin' : 'Member';
    document.getElementById('db-loading').style.display = 'none';
    document.getElementById('db-app').style.display = 'flex';
    listenForPigeons();
    listenForSeasons();
    listenForBreedingPairs();
    listenForTrainings();
    listenForRingSeries();
    listenForFeedTypes();
    listenForFeedings();
    listenForEvents();
    startCoreListeners();
    // Home is the new default landing tab (was Pigeon List, still marked
    // active in the static HTML as the pre-JS fallback) -- activateTab
    // here overrides that once everything's actually loaded.
    activateTab('home');
    // Shopify's own contact form redirects back here with this query param
    // once it's processed the submission -- pick it up to reopen Help and
    // confirm it went through, then strip the param so refreshing the page
    // doesn't keep re-showing "sent" for an old submission.
    if (justSubmittedContactForm) {
      activateTab('help');
      const banner = document.getElementById('help-contact-success-banner');
      banner.style.display = 'block';
      setTimeout(() => { banner.style.display = 'none'; }, 5000);
    }
    if (joinFlightGroupId) loadJoinFlightView(joinFlightGroupId);
    if (isAdmin) {
      document.getElementById('set-nav-admin').style.display = '';
      listenForAllowedEmails();
      listenForAllOrganizations();
    } else {
      // Admins are exempt from the tracker limit entirely (see
      // bindDeviceByMac), so there's nothing meaningful to fetch/show for
      // them here -- only regular accounts need to know their own cap.
      user.getIdToken().then(idToken =>
        fetch('https://us-central1-flightpathlive-24331.cloudfunctions.net/getMyTrackerLimit', {
          headers: { 'Authorization': 'Bearer ' + idToken }
        }).then(r => r.json())
      ).then(resp => {
        if (resp.ok) {
          myTrackerLimit = resp.limit;
          updateTrackerLimitDisplay();
        }
      }).catch(() => {}); // silently leave the display blank if this fails -- not worth blocking or alarming over
    }
  });

  // Settings/Dark Mode/Logout live in two places (sidebar bottom AND the
  // top-right avatar dropdown, both wired to the same underlying actions) --
  // ids can't repeat in the DOM, so the dropdown copies use a "-dd" suffix.
  const dropdown = document.getElementById('db-dropdown-menu');
  document.getElementById('db-avatar-toggle').onclick = (e) => {
    e.stopPropagation();
    dropdown.classList.toggle('open');
  };
  document.addEventListener('click', (e) => {
    if (!dropdown.contains(e.target)) dropdown.classList.remove('open');
    if (!e.target.closest('.ltc-kebab-btn')) {
      document.querySelectorAll('.ltc-kebab-menu.open').forEach(m => m.classList.remove('open'));
    }
  });

  // Shared by every .ltc-kebab-menu opener (Pigeon List, Trackers, and the
  // Pedigree tree's own boxes) -- for a card near the bottom of the
  // scrollable area, the menu's default "open downward from the button"
  // position ran past the bottom of the screen, effectively hidden below
  // the fold. Nudges it up by exactly however much it overflowed, rather
  // than a full flip-to-a-different-anchor, so it still opens right where
  // expected whenever there's already enough room.
  function keepKebabMenuInView(menu) {
    menu.style.marginTop = '';
    requestAnimationFrame(() => {
      const rect = menu.getBoundingClientRect();
      const overflow = rect.bottom - window.innerHeight;
      if (overflow > 0) menu.style.marginTop = (-(overflow + 10)) + 'px';
    });
  }
  // (Logout / dark-mode are no longer in the sidebar -- they're in Settings and the top-right account menu.)
  document.getElementById('db-logout-btn-dd').onclick = () => auth.signOut();

  function updateDarkModeLabel() {
    const isDark = document.body.classList.contains('dark-mode');
    const emoji = isDark ? '☀️' : '🌙';
    const text = isDark ? 'Toggle Light Mode' : 'Toggle Dark Mode';
    document.querySelector('#db-darkmode-toggle-btn-dd .darkmode-emoji-dd').textContent = emoji;
    document.querySelector('#db-darkmode-toggle-btn-dd .darkmode-label-dd').textContent = text;
  }
  function toggleDarkMode() {
    document.body.classList.toggle('dark-mode');
    localStorage.setItem('fpl-dark-mode', document.body.classList.contains('dark-mode') ? '1' : '0');
    updateDarkModeLabel();
    if (typeof syncThemeCards === 'function') syncThemeCards();
  }
  updateDarkModeLabel();
  document.getElementById('db-darkmode-toggle-btn-dd').onclick = () => {
    dropdown.classList.remove('open');
    toggleDarkMode();
  };

  // ---- PWA: installability only, no UI/behaviour change beyond the two hidden-by-default
  // dropdown rows added next to Settings/Dark Mode/Logout (see the liquid). Doesn't touch the
  // existing login check above (auth.onAuthStateChanged already shows "Loading..." then either
  // the dashboard or redirects to /pages/live-tracking, which is exactly the startup behaviour
  // a PWA needs -- nothing to add there).
  if ('serviceWorker' in navigator && window.FPL_SW_URL) {
    // No explicit {scope:'/'} here -- Shopify serves this file from its own assets sub-path
    // (see sw.js's own top comment for why), and requesting a wider scope than that from a
    // theme-only file would just throw. Registering with the default scope is the most this
    // setup can safely do. window.FPL_SW_URL is set by the liquid page (this .js file is a
    // plain asset -- Shopify never runs Liquid on it, so the URL has to be handed in this way).
    window.addEventListener('load', () => {
      navigator.serviceWorker.register(window.FPL_SW_URL).catch(() => {});
    });
  }
  let pwaDeferredInstallPrompt = null;
  const pwaInstallItem = document.getElementById('db-pwa-install-dd');
  const pwaIosTipItem = document.getElementById('db-pwa-ios-tip-dd');
  const pwaIsStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  const pwaIsIos = /iphone|ipad|ipod/i.test(navigator.userAgent) && !/crios|fxios/i.test(navigator.userAgent);
  // Home dashboard install nudge -- same underlying beforeinstallprompt/iOS state as the
  // account-dropdown row above, just surfaced more visibly. Each of the two banners (real
  // install button vs. iOS's manual-steps tip) remembers its own dismissal so closing one
  // doesn't hide the other, and either goes away for good once the app is actually installed.
  const pwaInstallBanner = document.getElementById('pwa-install-banner');
  const pwaInstallBannerBtn = document.getElementById('pwa-install-banner-btn');
  const pwaInstallBannerClose = document.getElementById('pwa-install-banner-close');
  const pwaIosBanner = document.getElementById('pwa-ios-banner');
  const pwaIosBannerClose = document.getElementById('pwa-ios-banner-close');
  function updatePwaInstallBannerVisibility() {
    if (pwaInstallBanner) {
      const dismissed = localStorage.getItem('fpl-pwa-install-banner-dismissed') === '1';
      pwaInstallBanner.style.display = (!pwaIsStandalone && !!pwaDeferredInstallPrompt && !dismissed) ? '' : 'none';
    }
    if (pwaIosBanner) {
      const dismissed = localStorage.getItem('fpl-pwa-ios-banner-dismissed') === '1';
      pwaIosBanner.style.display = (!pwaIsStandalone && pwaIsIos && !dismissed) ? '' : 'none';
    }
  }
  if (pwaInstallBannerClose) {
    pwaInstallBannerClose.onclick = () => {
      try { localStorage.setItem('fpl-pwa-install-banner-dismissed', '1'); } catch (e) {}
      updatePwaInstallBannerVisibility();
    };
  }
  if (pwaIosBannerClose) {
    pwaIosBannerClose.onclick = () => {
      try { localStorage.setItem('fpl-pwa-ios-banner-dismissed', '1'); } catch (e) {}
      updatePwaInstallBannerVisibility();
    };
  }
  if (pwaInstallBannerBtn) {
    pwaInstallBannerBtn.onclick = async () => {
      if (!pwaDeferredInstallPrompt) return;
      pwaDeferredInstallPrompt.prompt();
      await pwaDeferredInstallPrompt.userChoice;
      pwaDeferredInstallPrompt = null;
      if (pwaInstallItem) pwaInstallItem.style.display = 'none';
      updatePwaInstallBannerVisibility();
    };
  }
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    pwaDeferredInstallPrompt = e;
    if (pwaInstallItem) pwaInstallItem.style.display = '';
    updatePwaInstallBannerVisibility();
  });
  if (pwaInstallItem) {
    pwaInstallItem.onclick = async () => {
      dropdown.classList.remove('open');
      if (!pwaDeferredInstallPrompt) return;
      pwaDeferredInstallPrompt.prompt();
      await pwaDeferredInstallPrompt.userChoice;
      pwaDeferredInstallPrompt = null;
      pwaInstallItem.style.display = 'none';
      updatePwaInstallBannerVisibility();
    };
  }
  window.addEventListener('appinstalled', () => {
    pwaDeferredInstallPrompt = null;
    if (pwaInstallItem) pwaInstallItem.style.display = 'none';
    updatePwaInstallBannerVisibility();
  });
  // iOS Safari has no beforeinstallprompt at all -- "Add to Home Screen" is the only path, so
  // just point at it (a plain, non-clickable tip row) when running there and not installed yet.
  if (!pwaIsStandalone && pwaIosTipItem && pwaIsIos) {
    pwaIosTipItem.style.display = '';
  }
  updatePwaInstallBannerVisibility();

  // Club flights are ranked by each bird's arrival at its own loft, so both
  // the organiser (to create/share one) and anyone joining need a loft
  // location saved in Settings first.
  const GRP_LOFT_REQUIRED_MSG = 'Club flights rank each bird by when it reaches its own loft, so you need to set your loft location in Settings first.';
  function hasLoftLocation() {
    const l = currentOrg && currentOrg.homeLoft;
    return !!(l && typeof l.lat === 'number' && typeof l.lng === 'number');
  }
  async function goToLoftSettings() {
    await openSettingsPanel('loft');
    document.getElementById('loft-map').scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // Settings has its own left navigation (Account / Admin / Loft /
  // Appearance) inside the one Settings panel; only one pane shows at a
  // time. Admin is skipped for non-admins (its nav item is also hidden).
  function showSettingsTab(tab) {
    if (tab === 'admin' && !isAdmin) tab = 'account';
    document.querySelectorAll('#db-panel-settings .set-nav-item').forEach(b => b.classList.toggle('active', b.dataset.setTab === tab));
    document.querySelectorAll('#db-panel-settings .set-pane').forEach(p => p.classList.toggle('active', p.id === 'set-pane-' + tab));
    // The loft map is created while its pane may be hidden -- Leaflet needs
    // a re-measure once it's actually visible.
    if (tab === 'loft') setTimeout(() => { if (typeof refreshLoftMapView === 'function') refreshLoftMapView(); }, 50);
    if (tab === 'admin') loadAdminUsers();
    if (tab === 'appearance') syncThemeCards();
  }
  document.querySelectorAll('#db-panel-settings .set-nav-item').forEach(b => {
    b.onclick = () => showSettingsTab(b.dataset.setTab);
  });
  document.getElementById('set-logout-btn').onclick = () => auth.signOut();

  // Theme radio cards -- same switch as the sidebar's Toggle Dark Mode.
  function syncThemeCards() {
    const dark = document.body.classList.contains('dark-mode');
    document.getElementById('set-theme-light').classList.toggle('selected', !dark);
    document.getElementById('set-theme-dark').classList.toggle('selected', dark);
  }
  document.querySelectorAll('.set-theme-card').forEach(c => {
    c.onclick = () => {
      const wantDark = c.dataset.theme === 'dark';
      if (document.body.classList.contains('dark-mode') !== wantDark) toggleDarkMode();
      syncThemeCards();
    };
  });

  async function openSettingsPanel(tab) {
    // "More" on the mobile bottom bar isn't tied to any one panel (it just
    // opens the drawer), so it never gets highlighted active the way the
    // 3 real tabs do -- only the sidebar's own Settings item does.
    document.querySelectorAll('.db-nav-item, .db-mobile-tab').forEach(i => i.classList.remove('active'));
    document.getElementById('db-settings-menu-btn').classList.add('active');
    document.querySelectorAll('.db-panel').forEach(p => p.classList.remove('active'));
    document.getElementById('db-panel-settings').classList.add('active');
    const contentEl = document.querySelector('.db-content');
    if (contentEl) contentEl.scrollTop = 0;
    document.getElementById('db-settings-name').value = currentOrg.displayName || currentOrg.name || '';
    document.getElementById('db-settings-email').value = auth.currentUser.email || '';
    document.getElementById('db-settings-username').value = currentOrg.slug || '';
    document.getElementById('db-settings-role').value = isAdmin ? 'Admin' : 'Member';
    document.getElementById('db-settings-phone').value = currentOrg.phone || '';
    renderSettingsPhotos();
    showSettingsTab(typeof tab === 'string' ? tab : 'account');
    document.getElementById('db-settings-profile-status').textContent = '';
    document.getElementById('db-settings-pw-status').textContent = '';

    document.getElementById('loft-save-status').textContent = '';
    await ensureLoftMap();
    if (currentOrg.homeLoft) {
      setLoftPoint(currentOrg.homeLoft.lat, currentOrg.homeLoft.lng, true, true);
      updateLoftAddressFields({
        house_number: '', road: currentOrg.homeLoft.street,
        city: currentOrg.homeLoft.city, postcode: currentOrg.homeLoft.postcode, country: currentOrg.homeLoft.country
      });
    } else {
      clearLoftPoint();
    }
    setTimeout(refreshLoftMapView, 50);
  }
  document.getElementById('db-settings-menu-btn').onclick = () => {
    setMobileDrawer(false);
    openSettingsPanel();
  };
  document.getElementById('db-settings-menu-btn-dd').onclick = () => {
    dropdown.classList.remove('open');
    openSettingsPanel();
  };

  document.getElementById('db-settings-profile-save').onclick = async () => {
    const name = document.getElementById('db-settings-name').value.trim();
    const phone = document.getElementById('db-settings-phone').value.trim();
    const statusEl = document.getElementById('db-settings-profile-status');
    statusEl.textContent = 'Saving...';
    statusEl.style.color = '#6b7a87';
    try {
      const orgQuery = await db.collection('organizations').where('ownerUid', '==', auth.currentUser.uid).limit(1).get();
      if (!orgQuery.empty) {
        await orgQuery.docs[0].ref.update({ displayName: name, phone });
        currentOrg.displayName = name;
        currentOrg.phone = phone;
        applyAccountDisplayName();
      }
      statusEl.textContent = 'Saved!';
      statusEl.style.color = '#2e8b57';
    } catch (e) {
      statusEl.textContent = e.message;
      statusEl.style.color = '#c0392b';
    }
  };

  // ---- SETTINGS PHOTOS: profile photo + loft photo. Both are shrunk in the
  // browser (profile 256px square, loft max 1280x720, JPEG) and stored as a
  // small data URL on the account's own organizations doc (photoData /
  // loftPhotoData) -- the same doc + rule that already holds displayName and
  // homeLoft, so no Storage path or rule is involved. Each saves the moment
  // it's picked; the profile one also fills the top-bar avatar circle.
  function resizeImageToDataUrl(file, maxW, maxH, square) {
    return new Promise((resolve, reject) => {
      if (!file || !/^image\//.test(file.type)) return reject(new Error('Please choose an image file.'));
      if (file.size > 15 * 1024 * 1024) return reject(new Error('That image is too large (15 MB max).'));
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        let sx = 0, sy = 0, sw = img.width, sh = img.height;
        if (square) { const s = Math.min(sw, sh); sx = (sw - s) / 2; sy = (sh - s) / 2; sw = sh = s; }
        const scale = Math.min(1, maxW / sw, maxH / sh);
        const cw = Math.max(1, Math.round(sw * scale)), ch = Math.max(1, Math.round(sh * scale));
        const c = document.createElement('canvas');
        c.width = cw; c.height = ch;
        const ctx = c.getContext('2d');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, cw, ch);
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch);
        resolve(c.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Couldn't read that image.")); };
      img.src = url;
    });
  }

  async function saveOrgFields(fields) {
    const orgQuery = await db.collection('organizations').where('ownerUid', '==', auth.currentUser.uid).limit(1).get();
    if (orgQuery.empty) throw new Error('Account record not found.');
    await orgQuery.docs[0].ref.update(fields);
    Object.keys(fields).forEach(k => {
      if (fields[k] === null) delete currentOrg[k]; else currentOrg[k] = fields[k];
    });
  }

  function renderSettingsPhotos() {
    if (!currentOrg) return;
    const photo = currentOrg.photoData || '';
    const nameText = (document.getElementById('db-avatar-name').textContent || '').trim();
    const letter = nameText && nameText !== '—' ? nameText.charAt(0).toUpperCase() : '—';
    const av = document.getElementById('set-user-photo');
    av.style.backgroundImage = photo ? 'url("' + photo + '")' : '';
    const letterEl = document.getElementById('set-user-photo-letter');
    letterEl.textContent = letter;
    letterEl.style.display = photo ? 'none' : '';
    document.getElementById('set-user-photo-remove').hidden = !photo;
    const circle = document.getElementById('db-avatar-letter');
    circle.style.backgroundImage = photo ? 'url("' + photo + '")' : '';
    circle.style.backgroundSize = 'cover';
    circle.style.backgroundPosition = 'center';
    circle.textContent = photo ? '' : letter;
    const loft = currentOrg.loftPhotoData || '';
    document.getElementById('set-loft-photo').style.backgroundImage = loft ? 'url("' + loft + '")' : '';
    document.getElementById('set-loft-photo-empty').style.display = loft ? 'none' : '';
    document.getElementById('set-loft-photo-remove').hidden = !loft;
  }

  function wirePhotoPicker(cfg) {
    const statusEl = document.getElementById(cfg.status);
    const say = (msg, ok) => { statusEl.textContent = msg; statusEl.style.color = ok ? '#2e8b57' : '#c0392b'; };
    document.getElementById(cfg.btn).onclick = () => document.getElementById(cfg.file).click();
    document.getElementById(cfg.file).onchange = async (e) => {
      const file = e.target.files && e.target.files[0];
      e.target.value = '';
      if (!file) return;
      statusEl.textContent = 'Saving...';
      statusEl.style.color = '#6b7a87';
      try {
        const data = await resizeImageToDataUrl(file, cfg.maxW, cfg.maxH, cfg.square);
        await saveOrgFields({ [cfg.field]: data });
        renderSettingsPhotos();
        say(cfg.savedMsg, true);
      } catch (err) { say(err.message, false); }
    };
    document.getElementById(cfg.remove).onclick = async () => {
      statusEl.textContent = 'Removing...';
      statusEl.style.color = '#6b7a87';
      try {
        await saveOrgFields({ [cfg.field]: null });
        renderSettingsPhotos();
        say('Photo removed.', true);
      } catch (err) { say(err.message, false); }
    };
  }
  wirePhotoPicker({ btn: 'set-user-photo-btn', file: 'set-user-photo-file', remove: 'set-user-photo-remove', status: 'set-user-photo-status', field: 'photoData', maxW: 256, maxH: 256, square: true, savedMsg: 'Profile photo saved!' });
  wirePhotoPicker({ btn: 'set-loft-photo-btn', file: 'set-loft-photo-file', remove: 'set-loft-photo-remove', status: 'set-loft-photo-status', field: 'loftPhotoData', maxW: 1280, maxH: 720, square: false, savedMsg: 'Loft photo saved!' });
  // ---- END SETTINGS PHOTOS

  // Show/hide password -- Current/New/Confirm on Settings > Account. Each button only ever
  // toggles the type of the input right next to it (previousElementSibling), so this works
  // for any number of these without needing a separate handler per field.
  document.querySelectorAll('.set-pw-toggle').forEach(btn => {
    btn.onclick = () => {
      const input = btn.previousElementSibling;
      const showing = input.type === 'text';
      input.type = showing ? 'password' : 'text';
      btn.querySelector('i').className = showing ? 'fa-regular fa-eye' : 'fa-regular fa-eye-slash';
      btn.setAttribute('aria-label', showing ? 'Show password' : 'Hide password');
    };
  });

  document.getElementById('db-settings-pw-save').onclick = async () => {
    const currentPw = document.getElementById('db-settings-current-pw').value;
    const newPw = document.getElementById('db-settings-new-pw').value;
    const confirmPw = document.getElementById('db-settings-confirm-pw').value;
    const statusEl = document.getElementById('db-settings-pw-status');

    if (!currentPw || !newPw || !confirmPw) {
      statusEl.textContent = 'All fields are required.';
      statusEl.style.color = '#c0392b';
      return;
    }
    if (newPw.length < 6) {
      statusEl.textContent = 'New password must be at least 6 characters.';
      statusEl.style.color = '#c0392b';
      return;
    }
    if (newPw !== confirmPw) {
      statusEl.textContent = 'New password and confirm password do not match.';
      statusEl.style.color = '#c0392b';
      return;
    }

    statusEl.textContent = 'Updating...';
    statusEl.style.color = '#6b7a87';
    try {
      const credential = firebase.auth.EmailAuthProvider.credential(auth.currentUser.email, currentPw);
      await auth.currentUser.reauthenticateWithCredential(credential);
      await auth.currentUser.updatePassword(newPw);
      statusEl.textContent = 'Password updated!';
      statusEl.style.color = '#2e8b57';
      document.getElementById('db-settings-current-pw').value = '';
      document.getElementById('db-settings-new-pw').value = '';
      document.getElementById('db-settings-confirm-pw').value = '';
      // Hide again after a successful change, same as a freshly opened form.
      document.querySelectorAll('#db-panel-settings .set-input--pw input').forEach(inp => { inp.type = 'password'; });
      document.querySelectorAll('#db-panel-settings .set-pw-toggle').forEach(btn => {
        btn.querySelector('i').className = 'fa-regular fa-eye';
        btn.setAttribute('aria-label', 'Show password');
      });
    } catch (e) {
      statusEl.textContent = e.code === 'auth/wrong-password' ? 'Current password is incorrect.' : e.message;
      statusEl.style.color = '#c0392b';
    }
  };

  // Admin-only: emails allowed to sign up without needing a purchase on
  // file. Doc ID is the lowercased email itself, so the signup check can
  // look one up with a single get() instead of a query.
  function listenForAllowedEmails() {
    db.collection('allowedEmails').onSnapshot(snapshot => {
      const listEl = document.getElementById('db-allowed-emails-list');
      if (snapshot.empty) {
        listEl.innerHTML = '<div style="font-size:12px;color:#8a97a3;">No allowed emails yet.</div>';
        return;
      }
      listEl.innerHTML = '';
      snapshot.forEach(doc => {
        const row = document.createElement('div');
        row.className = 'allowed-email-row';
        const limit = doc.data().trackerLimit;
        // Clean by default (just a small "Limit: N/auto" label) -- clicking
        // the pencil swaps it for the actual editable number field, same
        // inline-edit pattern already used for pigeon/tracker fields
        // elsewhere, instead of a raw input sitting on every single row.
        row.innerHTML = `
          <span>${doc.data().email}</span>
          <span style="display:flex;align-items:center;gap:8px;">
            <span class="tracker-limit-label" style="font-size:11px;color:#8a97a3;">Limit: ${limit != null ? limit : 'auto'}</span>
            <i class="fa-solid fa-pen ltc-inline-edit-btn tracker-limit-edit-btn" title="Edit tracker limit" style="margin-left:0 !important;font-size:11px;"></i>
            <input type="number" min="0" class="tracker-limit-input" value="${limit != null ? limit : ''}" placeholder="auto" title="Tracker limit for this account" style="display:none;width:52px;padding:4px 6px;border:1px solid #ccc;border-radius:4px;font-size:12px;">
            <span class="remove">Remove</span>
          </span>
        `;
        const labelEl = row.querySelector('.tracker-limit-label');
        const editBtn = row.querySelector('.tracker-limit-edit-btn');
        const inputEl = row.querySelector('.tracker-limit-input');
        editBtn.onclick = () => {
          labelEl.style.display = 'none';
          editBtn.style.display = 'none';
          inputEl.style.display = 'inline-block';
          inputEl.focus();
          inputEl.select();
        };
        const saveAndCollapse = () => {
          const val = parseInt(inputEl.value, 10);
          db.collection('allowedEmails').doc(doc.id).set(
            { trackerLimit: isNaN(val) ? firebase.firestore.FieldValue.delete() : val },
            { merge: true }
          );
          labelEl.textContent = 'Limit: ' + (isNaN(val) ? 'auto' : val);
          inputEl.style.display = 'none';
          labelEl.style.display = '';
          editBtn.style.display = '';
        };
        inputEl.onblur = saveAndCollapse;
        inputEl.addEventListener('keydown', e => { if (e.key === 'Enter') inputEl.blur(); });
        row.querySelector('.remove').onclick = () => db.collection('allowedEmails').doc(doc.id).delete();
        listEl.appendChild(row);
      });
    });
  }

  document.getElementById('db-allowed-email-add').onclick = async () => {
    const input = document.getElementById('db-allowed-email-input');
    const statusEl = document.getElementById('db-allowed-email-status');
    const email = input.value.trim().toLowerCase();

    if (!email || !email.includes('@')) {
      statusEl.textContent = 'Enter a valid email address.';
      statusEl.style.color = '#c0392b';
      return;
    }
    try {
      await db.collection('allowedEmails').doc(email).set({
        email,
        addedAt: firebase.firestore.FieldValue.serverTimestamp()
      });
      input.value = '';
      statusEl.textContent = 'Added.';
      statusEl.style.color = '#2e8b57';
    } catch (e) {
      statusEl.textContent = e.message;
      statusEl.style.color = '#c0392b';
    }
  };

  // Static self-serve FAQ, drawn from the same knowledge base used to
  // answer support emails -- grouped by topic, searchable, accordion-style
  // (one question expanded at a time isn't enforced, any number can be
  // open together). Internal-only support content (staff tone-of-voice
  // rules, the universal unbind fallback password, etc.) is deliberately
  // left out of this list -- this is customer-facing only.
  const HELP_TOPICS = [
    {
      group: 'Getting Started', icon: 'fa-book-open',
      items: [
        { q: 'How do I set up my tracker for the first time?', a: `
          <ol style="margin:0;padding-left:18px;">
            <li>Download the app using the QR code on the box, or directly here:<br>
              <a href="https://play.google.com/store/apps/details?id=com.ht.blefinder" target="_blank" rel="noopener" style="color:#2f6fed;">Android</a>
              &middot;
              <a href="https://apps.apple.com/us/app/smarttag/id6502557491" target="_blank" rel="noopener" style="color:#2f6fed;">iPhone</a>
            </li>
            <li>Make sure Bluetooth is switched on on your phone.</li>
            <li>Put the tracker on charge for a few seconds to wake it up.</li>
            <li>Open the app and press "Bind" in the top-left corner.</li>
            <li>Wait a few seconds for the tracker to appear, then select it.</li>
            <li>Follow the instructions and add your bird/tracker name and contact information.</li>
            <li>Repeat for any additional trackers.</li>
          </ol>
        ` },
        { q: 'Why do I get an "Invalid QR code" error?', a: 'There\'s only one QR code — the one on the back of the box, which you scan with your phone\'s own camera to download the app. That\'s separate from the scan button you\'ll see inside the app when adding a device — if that one gives you an "Invalid QR code" error, just ignore it. Skip the scan button entirely and fill in the two fields directly instead: your contact information (the email or phone number used on your account) and the tracker\'s device number, which is printed on the tracker itself. Tap "Binding Device" to finish.' },
        { q: 'How do I track my bird on this website, not just the app?', a: 'Go to the "Live Tracking" page on our website and sign up using the same email you used for your order — no separate buyer ID needed. Once you\'re signed in, add your tracker\'s ID to start viewing live tracking here. Note: website tracking only works once the device has been bound with the Smarttag app at least once first.' },
        { q: 'Does the tracker use a SIM card or need internet?', a: 'No — the tracker doesn\'t use a SIM card and doesn\'t need its own internet connection.' },
      ]
    },
    {
      group: 'Pigeon Management', icon: 'fa-dove',
      items: [
        { q: 'How do I add a new pigeon?', a: 'Open Pigeon List and select + Add Pigeon. Enter the required pigeon details, then save the pigeon.' },
        { q: 'How do I edit a pigeon\'s information?', a: 'Open Pigeon List, use the ⋮ menu on the pigeon\'s card and choose Edit Pigeon. Change the details you need and save.' },
        { q: 'How do I assign a tracker to a pigeon?', a: 'Open Trackers, use the ⋮ menu on the tracker and choose Assign Pigeon, then pick the pigeon. You can also choose a tracker when you add or edit a pigeon.' },
        { q: 'What happens when I remove a pigeon?', a: 'Deleting a pigeon is permanent — the bird and its pedigree links are removed, and any tracker assigned to it becomes unassigned so it can be given to another pigeon. If a bird is just lost, dead or sold, set it to Inactive instead (Edit Pigeon → Status) to keep its record and pedigree.' },
        { q: 'How do I add a youngster from a breeding pair?', a: 'Open Breeding Pairs, open the pair and select + Add Youngster. Enter the youngster\'s details and save.' },
      ]
    },
    {
      group: 'Breeding', icon: 'fa-heart',
      items: [
        { q: 'How do I create a breeding pair?', a: 'Open Breeding Pairs and select + Add Breeding Pair. Choose the cock and the hen, then save the pair.' },
        { q: 'How do I record a youngster?', a: 'Open the breeding pair and select + Add Youngster. Enter the youngster\'s details and save — it is added to your Pigeon List.' },
        { q: 'How are parents recorded for a youngster?', a: 'When you add a youngster from a breeding pair, the pair\'s cock and hen are recorded as its parents, and they appear in the youngster\'s pedigree.' },
        { q: 'How do I view breeding history?', a: 'Open Breeding Pairs and select a pair. Its detail view shows the offspring, the pair\'s history and its notes.' },
      ]
    },
    {
      group: 'Training & Flights', icon: 'fa-flag',
      items: [
        { q: 'How do I create a training flight?', a: 'Open Trainings and select + Add Training. Enter the release point, date and time, distance and the participating pigeons, then save.' },
        { q: 'How do I record a pigeon\'s arrival?', a: 'Open Trainings and use Quick Log (or open the training\'s details) to log when each pigeon arrived. The flight time is worked out for you.' },
        { q: 'How is flight time calculated?', a: 'Flight time is calculated automatically: the pigeon\'s arrival time minus the training\'s release time.' },
        { q: 'What does the return rate mean?', a: 'Return rate is the percentage of pigeons sent on a training that arrived back (arrived ÷ sent). The Average Return Rate averages this across your completed trainings.' },
        { q: 'How do I view training history?', a: 'Open Trainings to see all your trainings. Use the search and filters to narrow them down, and open any training to see its details.' },
      ]
    },
    {
      group: 'Feeding', icon: 'fa-bowl-food',
      items: [
        { q: 'How do I record a feeding?', a: 'Open Feeding and select + Add Feeding. Choose the feed type, date and quantity, select the pigeons, then save.' },
        { q: 'How do I create a new feed type?', a: 'In the Add Feeding form, select + Add Feed Type, enter its details and save. The new feed type is then available to choose.' },
        { q: 'How do I assign feeding to selected pigeons?', a: 'In the Add Feeding form, choose which pigeons received the feed. Only the pigeons you select are recorded against that feeding.' },
        { q: 'How can I view feeding history?', a: 'Open Feeding to see every record. Use the Date, Feed Type and Pigeon filters to find specific feedings.' },
      ]
    },
    {
      group: 'Tracker & Group Tracking', icon: 'fa-tower-broadcast',
      items: [
        { q: 'How do I add a tracker?', a: 'Open Trackers and select + Add Tracker. Enter the tracker\'s MAC address and save. The tracker must have been bound once with the Smarttag app first.' },
        { q: 'How do I create a group flight?', a: 'Open Group Tracking and select + New Group. Enter the group details, select the trackers, and create the group. If you enable the Club Flight share link, a shareable link can be sent to other members.' },
        { q: 'What is a Club Flight?', a: 'A Club Flight is a group flight shared with other members. Each member adds their own birds, and everyone\'s birds appear together on the same map. Members only ever see positions from inside the flight\'s time window, and only for the birds each member chooses to enter.' },
        { q: 'How does the share/invite link work?', a: 'The organiser can enable the Club Flight share link after setting the required release point. Logged-in members who open the link can select their own trackers/pigeons and join the flight.' },
        { q: 'How do I join or withdraw from a Club Flight?', a: 'To join, open the share link while logged in, select your trackers and choose Add to Flight. To leave, open the flight from Club flights you\'ve joined (My Birds) and choose Withdraw from this flight. Your birds stop sharing new positions, and what was already recorded stays on the map.' },
        { q: 'Can I rejoin after withdrawing?', a: 'Yes. A member can join the flight again after withdrawing. Previously recorded flight positions remain part of the flight history.' },
      ]
    },
    {
      group: 'Statistics', icon: 'fa-chart-simple',
      items: [
        { q: 'How are the statistics calculated?', a: 'Statistics are calculated from your own records — pigeons, trainings, feeding, breeding and trackers — each time you open the page, so they update as you add data.' },
        { q: 'What does Average Return Rate mean?', a: 'It is the average of the return rates of your completed trainings, where each training\'s return rate is the share of the pigeons sent that arrived back.' },
        { q: 'How is Total Distance calculated?', a: 'Total Distance adds up the distances entered on your trainings.' },
        { q: 'What do the tracker statistics mean?', a: 'They show each tracked pigeon\'s last report, battery level and status. Active means the tracker reported within the last 48 hours; Calm means it hasn\'t reported in the last 48 hours (or never reported). Critical Battery is separate: it counts trackers whose battery is below 20%, whatever their status.' },
      ]
    },
    {
      group: 'Account and App', icon: 'fa-gear',
      items: [
        { q: 'How do I change my account details?', a: 'Open Settings and use Update Profile to change your name and phone number, or Change Password to set a new password. Your email address can\'t be edited there.' },
        { q: 'How do I change my loft information?', a: 'Open Settings, go to Loft Details, update the address or move the pin on the map, then select Save Loft Details.' },
        { q: 'How do I change language or theme?', a: 'There is no language setting. To switch between the light and dark theme, use Toggle Dark Mode in the sidebar.' },
        { q: 'How do I log out?', a: 'Select Logout at the bottom of the sidebar.' },
      ]
    },
    {
      group: 'Shipping and Customs', icon: 'fa-truck',
      items: [
        { q: 'How long does shipping take?', a: 'UK orders normally arrive the next working day where applicable. International orders typically take 5–7 business days once dispatched.' },
        { q: 'My parcel is stuck with customs — what do I do?', a: 'It\'s likely being processed by your local customs office. We\'re not able to speed up customs clearance — contact your local customs office directly with your tracking number for the latest update. Any customs duties or fees are set by your country\'s customs authority.' },
      ]
    },
    {
      group: 'Tracker Care & Troubleshooting', icon: 'fa-screwdriver-wrench',
      items: [
        { q: 'How long does the battery last?', a: 'Up to 30 days depending on usage. When there\'s no movement, the tracker automatically enters sleep mode to save battery, and wakes again as soon as movement is detected.' },
        { q: 'Is the tracker waterproof?', a: 'Yes — it\'s rated IP68.' },
        { q: 'How do I charge it?', a: 'Each tracker comes with its own magnetic charger included in the box — just attach it to charge.' },
        { q: 'What does the Call/Locate feature do?', a: 'It helps you find a nearby tracker: the app shows a stronger or weaker signal as you get closer, and can make the tracker flash to help you spot it. It works at short range once you\'re already near the tracker\'s last known location.' },
        { q: 'My tracker won\'t pair or bind.', a: 'Make sure the tracker is charged and Bluetooth is switched on. Try pairing just one tracker at a time. If it still doesn\'t work, repeat the bind process from the start. If it\'s still not working, contact support.' },
        { q: 'I haven\'t received tracking info yet.', a: 'Once your order has been processed by the courier, you should receive an email with tracking information. If it\'s only just been dispatched, it can take a little time for the courier to scan it into their system.' },
      ]
    },
  ];

  // Categorised help centre -- a grid of category cards, each an
  // accordion of questions (any number can be open together). Search
  // matches the question, its answer text, or the category name (a
  // category-name match shows all of that category's questions).
  function renderHelpTopics(term) {
    const contentEl = document.getElementById('help-content');
    const emptyEl = document.getElementById('help-empty');
    const t = (term || '').trim().toLowerCase();
    const plain = (html) => html.replace(/<[^>]*>/g, ' ').toLowerCase();
    let anyMatch = false;
    contentEl.innerHTML = HELP_TOPICS.map(topic => {
      const groupHit = !!t && topic.group.toLowerCase().includes(t);
      const items = topic.items.filter(it => !t || groupHit || it.q.toLowerCase().includes(t) || plain(it.a).includes(t));
      if (items.length === 0) return '';
      anyMatch = true;
      return `
        <div class="hlp-cat${t ? ' hlp-cat--open' : ''}">
          <div class="hlp-cat-head">
            <span class="hlp-cat-icon"><i class="fa-solid ${topic.icon}"></i></span>
            <h3>${topic.group}</h3>
            <span class="hlp-count">${items.length}</span>
            <i class="fa-solid fa-chevron-down hlp-cat-chev"></i>
          </div>
          <div class="hlp-qs">
            ${items.map(it => `
              <div class="help-card">
                <div class="help-q"><span>${it.q}</span><i class="fa-solid fa-chevron-down"></i></div>
                <div class="help-a">${it.a}</div>
              </div>
            `).join('')}
          </div>
        </div>
      `;
    }).join('');
    emptyEl.style.display = anyMatch ? 'none' : 'block';
    contentEl.querySelectorAll('.help-q').forEach(q => {
      q.onclick = () => q.parentElement.classList.toggle('open');
    });
    // On phones each category is a collapsible row (CSS hides its questions until it's open);
    // while searching every matching category starts open. No visible effect on desktop.
    contentEl.querySelectorAll('.hlp-cat-head').forEach(h => {
      h.onclick = () => h.parentElement.classList.toggle('hlp-cat--open');
    });
  }

  function applyHelpSearch() {
    renderHelpTopics(document.getElementById('help-search').value);
  }

  document.getElementById('help-search').oninput = () => applyHelpSearch();

  document.getElementById('help-contact-toggle-btn').onclick = () => {
    const form = document.getElementById('help-contact-form');
    form.reset();
    document.getElementById('help-contact-status').textContent = '';
    // Pre-fill from the signed-in account so the customer doesn't have to
    // retype an email we already know -- still an editable field though,
    // this is a real Shopify contact-form field now, not something
    // resolved server-side from an auth token.
    document.getElementById('help-contact-email').value = (auth.currentUser && auth.currentUser.email) || '';
    document.getElementById('help-contact-modal').classList.add('open');
  };

  document.getElementById('help-contact-cancel').onclick = () => {
    document.getElementById('help-contact-modal').classList.remove('open');
  };

  // Shopify's own contact form handler always redirects back to this page
  // (adding ?contact_posted=true to the URL) once it's processed -- fighting
  // that with a fetch-based interception turned out unreliable (Shopify's
  // own theme JS also reacts to this form), so this just lets it submit
  // natively. The reload lands back here with contact_posted=true, which
  // is picked up once the dashboard finishes loading (search this file for
  // "contact_posted") to reopen the Help tab and show a success message.
  document.getElementById('help-contact-send-btn').onclick = () => {
    document.getElementById('help-contact-status').textContent = 'Sending...';
    document.getElementById('help-contact-status').style.color = '#6b7a87';
  };

  // ---- SETTINGS -> ADMIN: user management. Talks to the admin-only Cloud
  // Functions adminListUsers / adminCreateUser / adminUpdateUser (server-
  // side admin check) -- hiding the tab for members is only cosmetic.
  // Role is display-only: who is an admin is fixed on the server.
  const ADMIN_FN_BASE = 'https://us-central1-flightpathlive-24331.cloudfunctions.net/';
  async function adminCall(fnName, body) {
    const idToken = await auth.currentUser.getIdToken();
    const resp = await fetch(ADMIN_FN_BASE + fnName, {
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + idToken, 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {})
    }).then(r => r.json());
    if (!resp.ok) throw new Error(resp.message || 'Request failed');
    return resp;
  }
  const admEsc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  let admUsers = [];
  let admEditingUid = null; // null = the modal is in Add mode
  let admPage = 1;
  let admPerPage = 10;
  let admSearchTerm = ''; // lower-cased text typed in the User Management search box

  async function loadAdminUsers() {
    if (!isAdmin) return;
    const statusEl = document.getElementById('adm-users-status');
    statusEl.textContent = 'Loading users…';
    statusEl.style.color = '#6b7a87';
    try {
      const resp = await adminCall('adminListUsers');
      admUsers = resp.users || [];
      statusEl.textContent = '';
      renderAdminUsers();
    } catch (e) {
      statusEl.textContent = 'Could not load users: ' + e.message;
      statusEl.style.color = '#c0392b';
    }
  }

  // "Trackers" column: used / allowed. Allowed = what they bought on Shopify (saved at their last login) or
  // an admin-set limit, plus the admin's extra grant. "—" = not known yet (user hasn't logged in since this
  // was added); admins have no limit.
  function admTrackersCell(u) {
    if (u.trackerUnlimited) return '<span class="adm-trk adm-trk--na" title="Admin accounts have no tracker limit">Unlimited</span>';
    const used = u.trackerUsed || 0;
    if (u.trackerLimit == null) return '<span class="adm-trk adm-trk--na" title="Allowed trackers appear after this user next logs in">' + used + ' / —</span>';
    const tip = used + ' used \u00b7 ' + (u.trackerBase == null ? 0 : u.trackerBase) + ' purchased/approved' + (u.trackerExtra ? ' + ' + u.trackerExtra + ' extra' : '');
    return '<span class="adm-trk' + (used >= u.trackerLimit ? ' adm-trk--full' : '') + '" title="' + tip + '"><b>' + used + '</b> / ' + u.trackerLimit + '</span>';
  }

  function renderAdminUsers() {
    const admins = admUsers.filter(u => u.role === 'Admin').length;
    document.getElementById('adm-stat-total').textContent = admUsers.length;
    document.getElementById('adm-stat-admins').textContent = admins;
    document.getElementById('adm-stat-members').textContent = admUsers.length - admins;
    document.getElementById('adm-stat-active').textContent = admUsers.filter(u => u.status === 'Active').length;
    const el = document.getElementById('adm-users');
    const footerEl = document.getElementById('adm-footer');
    if (admUsers.length === 0) {
      el.innerHTML = '<div style="padding:14px;color:#8a97a3;">No users yet.</div>';
      footerEl.style.display = 'none';
      return;
    }
    // search narrows the list by name or email; stat cards above keep counting everyone
    const filtered = admSearchTerm
      ? admUsers.filter(u => ((u.name || '') + ' ' + (u.email || '')).toLowerCase().includes(admSearchTerm))
      : admUsers;
    if (filtered.length === 0) {
      el.innerHTML = '<div style="padding:14px;color:#8a97a3;">No users match "' + admEsc(document.getElementById('adm-search').value.trim()) + '".</div>';
      footerEl.style.display = 'none';
      return;
    }
    footerEl.style.display = '';
    const totalPages = Math.max(1, Math.ceil(filtered.length / admPerPage));
    if (admPage > totalPages) admPage = totalPages;
    const start = (admPage - 1) * admPerPage;
    const pageUsers = filtered.slice(start, start + admPerPage);
    document.getElementById('adm-showing').textContent = 'Showing ' + (start + 1) + ' to ' + (start + pageUsers.length) + ' of ' + filtered.length + ' user' + (filtered.length !== 1 ? 's' : '');
    const pagEl = document.getElementById('adm-pagination');
    pagEl.innerHTML = '';
    if (totalPages > 1) {
      const addBtn = (label, page, opts) => {
        const b = document.createElement('button');
        b.textContent = label;
        if (opts && opts.disabled) b.disabled = true;
        if (opts && opts.active) b.className = 'active';
        b.onclick = () => { admPage = page; renderAdminUsers(); };
        pagEl.appendChild(b);
      };
      addBtn('Prev', admPage - 1, { disabled: admPage === 1 });
      for (let p = 1; p <= totalPages; p++) addBtn(String(p), p, { active: p === admPage });
      addBtn('Next', admPage + 1, { disabled: admPage === totalPages });
    }
    el.innerHTML = `<div class="adm-row adm-headrow"><div>User</div><div>Email</div><div>Trackers</div><div>Role</div><div>Status</div><div>Joined</div><div>Actions</div></div>` +
      pageUsers.map((u) => {
        const i = admUsers.indexOf(u); // index into the FULL list, not just this page/filter
        const name = admEsc(u.name || u.email || '—');
        const isAdminUser = u.role === 'Admin';
        const isActive = u.status === 'Active';
        return `<div class="adm-row" data-i="${i}">
          <div class="adm-user">${pigeonAvatarHtml({ name: u.name || u.email || '?', markerColor: '#1f4e9c' })}<b>${name}</b></div>
          <div class="adm-cell-email adm-muted">${admEsc(u.email)}</div>
          <div class="adm-cell-trackers"><i class="fa-solid fa-satellite-dish"></i>${admTrackersCell(u)}</div>
          <div class="adm-cell-role"><span class="adm-badge ${isAdminUser ? 'adm-badge--admin' : 'adm-badge--member'}">${u.role}</span></div>
          <div class="adm-cell-status"><span class="adm-badge ${isActive ? 'adm-badge--active' : 'adm-badge--inactive'}">${u.status}</span></div>
          <div class="adm-cell-joined adm-muted"><i class="fa-regular fa-calendar"></i>${u.joinedMs ? fmtDateOnly(u.joinedMs) : '—'}</div>
          <div class="adm-actions">
            <button type="button" class="adm-edit-btn"><i class="fa-solid fa-pen"></i> Edit</button>
            <button type="button" class="ltc-kebab-btn adm-kebab-btn" aria-label="More actions"><i class="fa-solid fa-ellipsis-vertical"></i></button>
            <div class="ltc-kebab-menu">
              <div class="ltc-kebab-menu-item adm-mob-edit-item"><i class="fa-solid fa-pen"></i> Edit User</div>
              ${isAdminUser ? '' : `<div class="ltc-kebab-menu-item adm-toggle-status"><i class="fa-solid ${isActive ? 'fa-user-slash' : 'fa-user-check'}"></i> ${isActive ? 'Deactivate' : 'Activate'}</div>`}
              <div class="ltc-kebab-menu-item adm-reset-pw"><i class="fa-solid fa-key"></i> Send password reset</div>
            </div>
          </div>
        </div>`;
      }).join('');
    el.querySelectorAll('.adm-row[data-i]').forEach(row => {
      const u = admUsers[+row.dataset.i];
      row.querySelector('.adm-edit-btn').onclick = () => openAdminUserModal(u);
      const kebab = row.querySelector('.adm-kebab-btn'), menu = row.querySelector('.ltc-kebab-menu');
      kebab.onclick = (e) => {
        e.stopPropagation();
        const wasOpen = menu.classList.contains('open');
        document.querySelectorAll('.ltc-kebab-menu.open').forEach(m => m.classList.remove('open'));
        if (!wasOpen) menu.classList.add('open');
      };
      // Mobile-only: the standalone Edit button is hidden on phones (see CSS), so the card's
      // ⋮ menu offers the same action here instead -- desktop never shows this item.
      row.querySelector('.adm-mob-edit-item').onclick = () => { menu.classList.remove('open'); openAdminUserModal(u); };
      const toggle = row.querySelector('.adm-toggle-status');
      if (toggle) toggle.onclick = async () => {
        menu.classList.remove('open');
        const next = u.status === 'Active' ? 'Inactive' : 'Active';
        if (next === 'Inactive' && !(await showConfirmModal(`${u.name || u.email} will no longer be able to sign in.`, 'Deactivate user?', 'Deactivate'))) return;
        try {
          await adminCall('adminUpdateUser', { uid: u.uid, status: next });
          showToast(next === 'Active' ? 'User activated' : 'User deactivated');
          loadAdminUsers();
        } catch (e) { showToast('Could not update: ' + e.message); }
      };
      row.querySelector('.adm-reset-pw').onclick = async () => {
        menu.classList.remove('open');
        try {
          await auth.sendPasswordResetEmail(u.email);
          showToast('Password reset email sent to ' + u.email);
        } catch (e) { showToast('Could not send: ' + e.message); }
      };
    });
  }

  function openAdminUserModal(user) {
    admEditingUid = user ? user.uid : null;
    document.getElementById('adm-user-modal-title').textContent = user ? 'Edit User' : 'Add User';
    document.getElementById('adm-user-modal-sub').textContent = user ? 'Update this user\'s details and status.' : 'Create a new user account.';
    document.getElementById('adm-f-name').value = user ? user.name : '';
    document.getElementById('adm-f-email').value = user ? user.email : '';
    document.getElementById('adm-f-email').disabled = !!user;
    document.getElementById('adm-f-username').value = user ? user.username : '';
    document.getElementById('adm-f-username').disabled = !!user;
    document.getElementById('adm-f-password').value = '';
    document.getElementById('adm-f-password-wrap').style.display = user ? 'none' : '';
    document.getElementById('adm-f-phone').value = user ? user.phone : '';
    document.getElementById('adm-f-phone-wrap').style.display = user ? '' : 'none';
    document.getElementById('adm-f-role').value = user ? user.role : 'Member';
    const extraWrap = document.getElementById('adm-f-extra-wrap');
    extraWrap.style.display = (user && user.role !== 'Admin') ? '' : 'none';
    document.getElementById('adm-f-extra').value = user ? (user.trackerExtra || 0) : '';
    document.getElementById('adm-f-extra-hint').textContent = user
      ? 'Allowed = purchased/approved (' + (user.trackerBase == null ? 'not known yet' : user.trackerBase) + ') + this extra. Currently using ' + (user.trackerUsed || 0) + '.'
      : '';
    const statusSel = document.getElementById('adm-f-status');
    statusSel.value = user ? user.status : 'Active';
    statusSel.disabled = !!(user && user.role === 'Admin'); // admin accounts can't be deactivated
    // Delete is Edit-only and never offered for an admin account.
    document.getElementById('adm-user-delete').style.display = (user && user.role !== 'Admin') ? '' : 'none';
    document.getElementById('adm-user-status').textContent = '';
    document.getElementById('adm-user-modal').classList.add('open');
  }
  document.getElementById('adm-search').oninput = (e) => { admSearchTerm = e.target.value.trim().toLowerCase(); admPage = 1; renderAdminUsers(); };
  document.getElementById('adm-per-page').onchange = (e) => { admPerPage = parseInt(e.target.value, 10); admPage = 1; renderAdminUsers(); };
  document.getElementById('adm-add-btn').onclick = () => openAdminUserModal(null);

  // "Refresh from Shopify": re-reads each user's purchased-tracker count and saves it (the Trackers
  // column then shows real numbers for people who haven't logged in yet). Done in batches of 10, one
  // after another, so a big list never times out or trips Shopify's rate limit; the same button
  // becomes "Stop" while it runs.
  let admRefreshing = false, admRefreshStop = false;
  document.getElementById('adm-refresh-btn').onclick = async () => {
    const label = document.getElementById('adm-refresh-label');
    const statusEl = document.getElementById('adm-users-status');
    if (admRefreshing) { admRefreshStop = true; label.textContent = 'Stopping…'; return; }
    const targets = admUsers.filter(u => u.uid && u.role !== 'Admin').map(u => u.uid);
    if (!targets.length) { showToast('No users to refresh'); return; }
    const mins = Math.max(1, Math.ceil(targets.length * 0.7 / 60));
    if (!(await showConfirmModal(
      'This re-checks Shopify for ' + targets.length + ' users and saves how many trackers each one bought. It takes about ' + mins + ' minute' + (mins === 1 ? '' : 's') + ' — keep this page open. You can press Stop at any time.',
      'Refresh from Shopify?', 'Refresh'))) return;
    admRefreshing = true; admRefreshStop = false; label.textContent = 'Stop';
    let done = 0, ok = 0, skipped = 0;
    const errors = [];
    try {
      for (let i = 0; i < targets.length && !admRefreshStop; i += 10) {
        const chunk = targets.slice(i, i + 10);
        statusEl.textContent = 'Refreshing from Shopify… ' + done + ' / ' + targets.length;
        statusEl.style.color = '#6b7a87';
        try {
          const resp = await adminCall('adminRefreshTrackerAllowances', { uids: chunk });
          resp.results.forEach(r => {
            if (r.status === 'ok') ok++;
            else if (r.status === 'skipped') skipped++;
            else errors.push(r.message || 'failed');
          });
        } catch (e) { chunk.forEach(() => errors.push(e.message)); }
        done += chunk.length;
      }
    } finally {
      admRefreshing = false;
      label.textContent = 'Refresh from Shopify';
    }
    const stopped = admRefreshStop;
    await loadAdminUsers(); // reload first -- it clears the status line
    statusEl.textContent = (stopped ? 'Stopped after ' + done + ' of ' + targets.length + '. ' : 'Done. ') + ok + ' updated'
      + (skipped ? ', ' + skipped + ' skipped (manual limit)' : '')
      + (errors.length ? ', ' + errors.length + ' failed (' + errors[0] + ')' : '') + '.';
    statusEl.style.color = errors.length ? '#c0392b' : '#2e8b57';
    showToast(errors.length ? 'Refresh finished with ' + errors.length + ' failure(s)' : 'Tracker counts refreshed');
  };
  document.getElementById('adm-user-delete').onclick = async () => {
    const u = admUsers.find(x => x.uid === admEditingUid);
    if (!u) return;
    if (!(await showConfirmModal(
      `This permanently deletes ${u.name || u.email}'s account and all their data: pigeons, breeding pairs, seasons, trainings, feeding records, events, ring series, group flights and their login. Their trackers and position history are kept. This can't be undone.`,
      'Delete account?',
      'Delete account'
    ))) return;
    const statusEl = document.getElementById('adm-user-status');
    statusEl.textContent = 'Deleting…';
    statusEl.style.color = '#6b7a87';
    try {
      // Same admin-only function the "Remove Account by Email" tool uses.
      await adminCall('removeAccountByEmail', { email: u.email, uid: u.uid });
      document.getElementById('adm-user-modal').classList.remove('open');
      showToast('Account deleted');
      loadAdminUsers();
    } catch (e) {
      statusEl.textContent = e.message;
      statusEl.style.color = '#c0392b';
    }
  };
  document.getElementById('adm-user-cancel').onclick = () => document.getElementById('adm-user-modal').classList.remove('open');
  document.querySelector('#adm-user-modal .db-modal-close').onclick = () => document.getElementById('adm-user-modal').classList.remove('open');
  document.getElementById('adm-user-save').onclick = async () => {
    const statusEl = document.getElementById('adm-user-status');
    const name = document.getElementById('adm-f-name').value.trim();
    const email = document.getElementById('adm-f-email').value.trim();
    const status = document.getElementById('adm-f-status').value;
    const fail = (msg) => { statusEl.textContent = msg; statusEl.style.color = '#c0392b'; };
    if (!name) return fail('Enter a full name.');
    try {
      statusEl.textContent = 'Saving…';
      statusEl.style.color = '#6b7a87';
      if (admEditingUid) {
        const body = { uid: admEditingUid, name, phone: document.getElementById('adm-f-phone').value, status };
        if (document.getElementById('adm-f-extra-wrap').style.display !== 'none') {
          const extraRaw = document.getElementById('adm-f-extra').value.trim();
          const extraN = extraRaw === '' ? 0 : Number(extraRaw);
          if (!Number.isInteger(extraN) || extraN < 0 || extraN > 10000) return fail('Extra trackers must be a whole number, 0 or more.');
          body.extraTrackers = extraN;
        }
        await adminCall('adminUpdateUser', body);
      } else {
        const password = document.getElementById('adm-f-password').value;
        if (!email) return fail('Enter an email.');
        if (password.length < 6) return fail('Password must be at least 6 characters.');
        await adminCall('adminCreateUser', { name, email, username: document.getElementById('adm-f-username').value.trim(), password, status });
      }
      document.getElementById('adm-user-modal').classList.remove('open');
      showToast(admEditingUid ? 'User updated' : 'User created');
      loadAdminUsers();
    } catch (e) { fail(e.message); }
  };

  document.getElementById('db-order-lookup-btn').onclick = async () => {
    const input = document.getElementById('db-order-lookup-input');
    const statusEl = document.getElementById('db-order-lookup-status');
    const resultsEl = document.getElementById('db-order-lookup-results');
    const term = input.value.trim();
    resultsEl.innerHTML = '';
    if (!term) {
      statusEl.textContent = 'Enter an email or order number.';
      statusEl.style.color = '#c0392b';
      return;
    }
    // Same "@" heuristic used elsewhere in this file to tell an email apart
    // from a bare id -- an order number never contains one.
    const isEmail = term.includes('@');
    statusEl.textContent = 'Searching...';
    statusEl.style.color = '#6b7a87';
    try {
      const idToken = await auth.currentUser.getIdToken();
      const params = isEmail ? `email=${encodeURIComponent(term.toLowerCase())}` : `orderNumber=${encodeURIComponent(term)}`;
      const resp = await fetch(`https://us-central1-flightpathlive-24331.cloudfunctions.net/lookupShopifyOrder?${params}`, {
        headers: { 'Authorization': 'Bearer ' + idToken }
      }).then(r => r.json());

      if (!resp.ok) {
        statusEl.textContent = resp.message;
        statusEl.style.color = '#c0392b';
        return;
      }
      if (resp.orders.length === 0) {
        statusEl.textContent = 'No matching orders found.';
        statusEl.style.color = '#8a97a3';
        return;
      }
      statusEl.textContent = '';
      // Only 3 badge color buckets exist (paid/fulfilled=green, pending/
      // partial=amber, unpaid/unfulfilled/cancelled/refunded=red) -- map
      // Shopify's extra shipment_status values onto those same buckets
      // instead of adding a CSS class per status.
      const SHIPMENT_STATUS_BUCKET = {
        delivered: 'fulfilled',
        confirmed: 'partial', in_transit: 'partial', out_for_delivery: 'partial',
        label_printed: 'partial', label_purchased: 'partial', ready_for_pickup: 'partial',
        attempted_delivery: 'unfulfilled', failure: 'unfulfilled'
      };
      const badgeClass = (status) => {
        const key = (status || 'pending').toLowerCase();
        return 'ord-badge ord-badge--' + (SHIPMENT_STATUS_BUCKET[key] || key);
      };
      const renderAddress = (addr, fallbackName) => addr ? `
        <div class="ord-line" style="display:block;">
          ${addr.name || fallbackName || ''}<br>
          ${addr.address1 || ''}${addr.address2 ? ', ' + addr.address2 : ''}<br>
          ${[addr.city, addr.province, addr.zip].filter(Boolean).join(', ')}<br>
          ${addr.country || ''}${addr.phone ? ' · ' + addr.phone : ''}
        </div>
      ` : '<div class="ord-line">Not on file</div>';
      resultsEl.innerHTML = resp.orders.map(o => {
        const billingSameAsShipping = o.shippingAddress && o.billingAddress &&
          JSON.stringify(o.shippingAddress) === JSON.stringify(o.billingAddress);
        return `
        <div class="ord-card">
          <div class="ord-title">${o.name} ${o.customerName ? '— ' + o.customerName : ''}</div>
          <div class="ord-meta">
            ${new Date(o.createdAt).toLocaleString()} · ${o.email || '—'} · ${o.currency || ''} ${o.totalPrice || ''}
            <span class="${badgeClass(o.financialStatus)}">${o.financialStatus || 'pending'}</span>
            <span class="${badgeClass(o.fulfillmentStatus)}">${o.fulfillmentStatus || 'unfulfilled'}</span>
          </div>
          <div class="ord-section-label">Items</div>
          ${o.lineItems.map(li => `<div class="ord-line"><span>${li.title}</span><span>x${li.quantity}</span></div>`).join('') || '<div class="ord-line">—</div>'}
          <div class="ord-section-label">Shipping address</div>
          ${renderAddress(o.shippingAddress, o.customerName)}
          <div class="ord-section-label">Billing address</div>
          ${billingSameAsShipping ? '<div class="ord-line">Same as shipping address</div>' : renderAddress(o.billingAddress, o.customerName)}
          <div class="ord-section-label">Shipping tracking</div>
          ${o.fulfillments.length === 0 ? '<div class="ord-line">Not yet shipped</div>' : o.fulfillments.map(f => `
            <div class="ord-line">
              <span>${f.trackingCompany || 'Carrier —'} ${f.trackingNumber ? '· ' + f.trackingNumber : ''}</span>
              <span>${f.trackingUrl ? `<a href="${f.trackingUrl}" target="_blank" rel="noopener" class="ord-track-btn">Track</a>` : (f.status || '—')}</span>
            </div>
            <div class="ord-line">
              <span style="color:#8a97a3;">Delivery status</span>
              <span>${f.shipmentStatus ? `<span class="${badgeClass(f.shipmentStatus)}">${f.shipmentStatus.replace(/_/g, ' ')}</span>` : '<span style="color:#8a97a3;">Not available for this carrier</span>'}</span>
            </div>
            <div class="ord-line">
              <span style="color:#8a97a3;">Shipped</span>
              <span>${f.createdAt ? new Date(f.createdAt).toLocaleString() : '—'}</span>
            </div>
            ${f.shipmentStatus === 'delivered' ? `
              <div class="ord-line">
                <span style="color:#8a97a3;">Delivered</span>
                <span>${f.updatedAt ? new Date(f.updatedAt).toLocaleString() : '—'}</span>
              </div>
            ` : ''}
          `).join('')}
        </div>
      `;
      }).join('');
    } catch (e) {
      statusEl.textContent = e.message;
      statusEl.style.color = '#c0392b';
    }
  };

  const dbSidebar = document.querySelector('.db-sidebar');
  const navToggleBtn = document.getElementById('db-nav-toggle-btn');
  // One place that opens/closes the mobile drawer (hamburger, More, the dim
  // backdrop, picking a tab and Settings all go through it).
  function setMobileDrawer(open) {
    dbSidebar.classList.toggle('mobile-open', open);
    navToggleBtn.classList.toggle('is-open', open);
    navToggleBtn.textContent = open ? '✕' : '☰';
    navToggleBtn.setAttribute('aria-label', open ? 'Hide menu' : 'Show menu');
  }
  navToggleBtn.onclick = () => setMobileDrawer(!dbSidebar.classList.contains('mobile-open'));
  const drawerBackdrop = document.getElementById('db-drawer-backdrop');
  if (drawerBackdrop) drawerBackdrop.onclick = () => setMobileDrawer(false);

  // [data-tab] scopes this to actual tabs only -- Settings/Toggle Dark
  // Mode/Logout share the .db-nav-item class for matching sidebar styling,
  // but have their own specific click handlers (wired above) and no
  // data-tab, so this generic tab-switcher must skip them, not overwrite
  // those handlers with activateTab(undefined). Also covers .db-mobile-tab
  // (the mobile bottom bar's Pigeons/Trackers/Groups buttons).
  document.querySelectorAll('.db-nav-item[data-tab], .db-mobile-tab[data-tab]').forEach(item => {
    item.onclick = () => {
      activateTab(item.dataset.tab);
      // Close the drawer after picking a tab, on mobile.
      setMobileDrawer(false);
    };
  });
  // "More" on the mobile bottom bar opens the same hamburger drawer as the
  // ☰ button -- gives access to Settings, Dark Mode, Logout, and (for
  // admin) the extra tools, without needing a 5th/6th bottom-bar slot.
  document.getElementById('db-mobile-more-btn').onclick = () => navToggleBtn.click();

  // Shortcut to the Help tab from the topbar, next to the avatar -- same
  // destination as the sidebar's own Help item, just reachable without
  // opening the sidebar/drawer first.
  document.getElementById('db-help-topbar-btn').onclick = () => activateTab('help');

  const modal = document.getElementById('db-pigeon-modal');
  let editingPigeonId = null;
  // Pigeon Pedigree page state -- see the full implementation further down
  // (search PIGEON PEDIGREE PAGE). pedigreeSubject is whichever pigeon is
  // currently centered as Generation 0; the rest remember the page's own
  // controls across re-renders (recentering on an ancestor keeps them as-is
  // rather than resetting to defaults).
  let pedigreeSubject = null; // { id, p }
  let pedigreeGenerations = 4; // 2, 3 or 4 -- how many generations (incl. the subject) are shown
  let pedigreeActiveSubTab = 'offspring'; // offspring | siblings | training | notes
  let pedigreeEntryTab = 'pigeon-list'; // which tab the back arrow / "Pigeons" breadcrumb returns to
  let editingExistingPhotoURL = null;

  // A wide, hand-picked palette -- each color kept saturated/dark enough to
  // stay visible against the map tiles (no pale pastels that would wash out).
  const MARKER_COLOR_PALETTE = [
    { name: 'Blue', hex: '#2f6fed' },
    { name: 'Red', hex: '#e05a5a' },
    { name: 'Green', hex: '#2e8b57' },
    { name: 'Purple', hex: '#c084fc' },
    { name: 'Orange', hex: '#f2a65a' },
    { name: 'Teal', hex: '#5ad1c8' },
    { name: 'Pink', hex: '#ff6fa3' },
    { name: 'Yellow', hex: '#e8c93d' },
    { name: 'Navy', hex: '#1a3a6b' },
    { name: 'Maroon', hex: '#8b2c2c' },
    { name: 'Olive', hex: '#6b7a1f' },
    { name: 'Lime', hex: '#8fd14f' },
    { name: 'Cyan', hex: '#2ec4e8' },
    { name: 'Magenta', hex: '#d13fd1' },
    { name: 'Brown', hex: '#8a5a34' },
    { name: 'Gold', hex: '#d4a017' },
    { name: 'Coral', hex: '#ff7f5e' },
    { name: 'Salmon', hex: '#f28080' },
    { name: 'Indigo', hex: '#4b3fa0' },
    { name: 'Violet', hex: '#9b59d9' },
    { name: 'Turquoise', hex: '#30c9b0' },
    { name: 'Crimson', hex: '#d21e4a' },
    { name: 'Forest Green', hex: '#1f6b3a' },
    { name: 'Sky Blue', hex: '#6ab7e8' },
    { name: 'Hot Pink', hex: '#ff2e93' },
    { name: 'Dark Orange', hex: '#e07a1f' },
    { name: 'Slate Blue', hex: '#6a6fd1' },
    { name: 'Sea Green', hex: '#2ea36b' },
    { name: 'Orchid', hex: '#c15bd1' },
    { name: 'Tomato', hex: '#e8452f' },
    { name: 'Steel Blue', hex: '#3a7ea3' },
    { name: 'Amber', hex: '#ffa726' },
    { name: 'Emerald', hex: '#16a870' },
    { name: 'Ruby', hex: '#c2185b' },
    { name: 'Sapphire', hex: '#1560bd' },
    { name: 'Chartreuse', hex: '#a8d13f' },
    { name: 'Plum', hex: '#9b3f8a' },
    { name: 'Rust', hex: '#b23c1f' },
    { name: 'Mint', hex: '#4fd1a5' },
    { name: 'Lavender', hex: '#a58fd1' }
  ];
  let selectedMarkerColor = MARKER_COLOR_PALETTE[0].hex;
  let selectedSex = 'unknown';
  let selectedArchived = false;

  // No more manual color picker in the form -- each new pigeon just gets
  // the first palette color not already used by one of this org's own
  // pigeons (falls back to a random one once all 40 are taken), so map
  // markers stay distinguishable without asking the user to pick one.
  // Editing an existing pigeon keeps whatever color it already has.
  function pickAutoMarkerColor() {
    const usedColors = new Set(
      allPigeonDocs.filter(d => d.p.orgId === currentOrg.slug).map(d => d.p.markerColor).filter(Boolean)
    );
    const unused = MARKER_COLOR_PALETTE.find(c => !usedColors.has(c.hex));
    return (unused || MARKER_COLOR_PALETTE[Math.floor(Math.random() * MARKER_COLOR_PALETTE.length)]).hex;
  }

  function renderSegmentedGroup(containerId, selectedValue) {
    document.querySelectorAll('#' + containerId + ' button').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.value === selectedValue);
    });
  }

  document.querySelectorAll('#db-pigeon-sex-group button').forEach(btn => {
    btn.onclick = () => { selectedSex = btn.dataset.value; renderSegmentedGroup('db-pigeon-sex-group', selectedSex); };
  });

  document.querySelectorAll('#db-pigeon-archived-group button').forEach(btn => {
    btn.onclick = () => {
      selectedArchived = btn.dataset.value === 'archived';
      renderSegmentedGroup('db-pigeon-archived-group', btn.dataset.value);
    };
  });

  // Fills the Cock/Hen parent dropdowns from this org's own pigeons only
  // (a parent link across accounts wouldn't make sense -- pedigree is
  // per-loft), excluding the pigeon being edited itself so it can't be set
  // as its own parent.
  // Everyone who can be picked as a sire (cock) or dam (hen): this loft's pigeons AND the
  // ancestors saved inside imported pedigrees. Shown as  RING (Name)  and sorted by ring.
  // An imported ancestor whose ring is already a real pigeon here is skipped (no twins).
  function parentChoices(sex, excludePigeonId) {
    const own = allPigeonDocs.filter(d => d.p.orgId === currentOrg.slug);
    const label = p => {
      const ring = String(p.ringNumber || '').trim(), name = String(p.name || '').trim();
      return ring ? (name && name !== ring ? ring + ' (' + name + ')' : ring) : (name || 'No ring number');
    };
    const out = [], seen = {};
    own.forEach(d => {
      if (d.id === excludePigeonId || d.p.sex !== sex) return;
      out.push({ value: d.id, label: label(d.p) });
      const k = pdiNorm(d.p.ringNumber); if (k) seen[k] = true;
    });
    own.forEach(main => {
      const anc = main.p.pedigreeAncestors;
      if (!anc) return;
      Object.keys(anc).forEach(key => {
        const a = anc[key];
        if (!a || a.sex !== sex) return;
        const k = pdiNorm(a.ringNumber);
        if (k && seen[k]) return;
        if (k) seen[k] = true;
        out.push({ value: 'anc:' + main.id + ':' + key, label: label(a) });
      });
    });
    out.sort((x, y) => x.label.localeCompare(y.label, undefined, { numeric: true, sensitivity: 'base' }));
    return out;
  }
  const parentOptionsHtml = list => list.map(o => '<option value="' + o.value + '">' + String(o.label).replace(/&/g, '&amp;').replace(/</g, '&lt;') + '</option>').join('');

  // Colour and Strain/Family: the fields stay free text, but they now offer every value already
  // used in this loft (and in imported pedigrees) as a pick list -- so nobody retypes a strain or
  // colour twice. Colours also start with the common racing-pigeon colours.
  const COMMON_PIGEON_COLORS = ['Blue Bar', 'Blue Check', 'Blue Chequer', 'Red Bar', 'Red Check', 'Red Chequer', 'Black', 'White', 'Grizzle', 'Mealy', 'Dun', 'Brown', 'Spread', 'Pied', 'Recessive Red', 'Silver', 'Ash Red'];
  function refreshColorStrainSuggestions() {
    const own = allPigeonDocs.filter(d => d.p.orgId === currentOrg.slug);
    const collect = (field, seed) => {
      const seen = {}, out = [];
      const add = v => { const t = String(v || '').trim(); const k = t.toLowerCase(); if (t && !seen[k]) { seen[k] = true; out.push(t); } };
      own.forEach(d => {
        add(d.p[field]);
        const anc = d.p.pedigreeAncestors;
        if (anc) Object.keys(anc).forEach(k => add(anc[k] && anc[k][field]));
      });
      (seed || []).forEach(add);
      return out.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    };
    const fill = (id, values) => { const el = document.getElementById(id); if (el) el.innerHTML = values.map(v => '<option value="' + String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;') + '"></option>').join(''); };
    fill('db-pigeon-color-list', collect('color', COMMON_PIGEON_COLORS));
    fill('db-pigeon-strain-list', collect('strainFamily', []));
  }

  function populateParentSelects(excludePigeonId) {
    refreshColorStrainSuggestions();
    document.getElementById('db-pigeon-sire').innerHTML = '<option value="">Unknown</option>' + parentOptionsHtml(parentChoices('cock', excludePigeonId));
    document.getElementById('db-pigeon-dam').innerHTML = '<option value="">Unknown</option>' + parentOptionsHtml(parentChoices('hen', excludePigeonId));
  }

  // ---- Add/Edit Pigeon modal extras (photo preview + drop zone, description
  // counter, collapsible Parents card, optional Tracker dropdown). All of it
  // sits on top of the same field ids the save handler already reads.
  let editingExistingTrackerId = null;
  let pigeonTrackerInitial = '';

  function setPhotoPreview(url) {
    const el = document.getElementById('db-pigeon-photo-preview');
    if (!el) return;
    el.innerHTML = url ? '<img src="' + url + '" alt="">' : '<i class="fa-solid fa-dove"></i>';
  }
  (function wirePigeonPhotoDrop() {
    const drop = document.getElementById('db-pigeon-photo-drop');
    const input = document.getElementById('db-pigeon-photo');
    if (!drop || !input) return;
    const MAX_BYTES = 5 * 1024 * 1024;
    function acceptFile(file) {
      if (!file) return;
      if (!/^image\//.test(file.type)) { showToast('Please choose an image (JPG or PNG).'); input.value = ''; return; }
      if (file.size > MAX_BYTES) { showToast('That photo is over 5MB -- please choose a smaller one.'); input.value = ''; return; }
      setPhotoPreview(URL.createObjectURL(file));
    }
    drop.addEventListener('click', e => { if (e.target !== input) input.click(); });
    input.addEventListener('change', () => {
      const f = input.files && input.files[0];
      if (f) acceptFile(f); else setPhotoPreview(editingExistingPhotoURL);
    });
    ['dragenter', 'dragover'].forEach(ev => drop.addEventListener(ev, e => { e.preventDefault(); drop.classList.add('dragover'); }));
    ['dragleave', 'drop'].forEach(ev => drop.addEventListener(ev, e => { e.preventDefault(); drop.classList.remove('dragover'); }));
    drop.addEventListener('drop', e => {
      const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (!f) return;
      try { const dt = new DataTransfer(); dt.items.add(f); input.files = dt.files; } catch (err) { /* older browsers: fall back to click-to-choose */ }
      acceptFile(f);
    });
  })();

  function updateShortDescCount() {
    const el = document.getElementById('db-pigeon-shortdesc-count');
    if (el) el.textContent = document.getElementById('db-pigeon-shortdesc').value.length;
  }
  document.getElementById('db-pigeon-shortdesc').addEventListener('input', updateShortDescCount);

  function setParentsCardOpen(open) {
    const card = document.getElementById('pgn-parents-card');
    if (card) card.classList.toggle('open', !!open);
  }
  (function wireParentsToggle() {
    const head = document.getElementById('pgn-parents-toggle');
    if (!head) return;
    const toggle = () => document.getElementById('pgn-parents-card').classList.toggle('open');
    head.addEventListener('click', toggle);
    head.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } });
  })();

  // Same rules as the Assign tracker dialog: regular accounts only see their
  // own unassigned trackers; admin sees every tracker (labelled when it's
  // already on another pigeon). The pigeon's own current tracker is always
  // offered so editing doesn't silently drop it.
  function populatePigeonTrackerSelect(currentTrackerId) {
    const sel = document.getElementById('db-pigeon-tracker');
    if (!sel) return;
    const opts = ['<option value="">No tracker assigned</option>'];
    Object.keys(registry).forEach(rawId => {
      const isCurrent = rawId === currentTrackerId;
      const available = isAdmin ? true : (!!myTrackerMemberships[rawId] && !registry[rawId].assignedPigeonId);
      if (!isCurrent && !available) return;
      const nickname = registry[rawId].nickname;
      const takenBy = !isCurrent && registry[rawId].assignedPigeonId
        ? allPigeonDocs.find(d => d.id === registry[rawId].assignedPigeonId) : null;
      const takenLabel = registry[rawId].assignedPigeonId && !isCurrent ? ' -- currently on ' + (takenBy ? takenBy.p.name : 'another pigeon') : '';
      opts.push('<option value="' + rawId + '">' + (nickname ? rawId + ' — ' + nickname : rawId) + takenLabel + '</option>');
    });
    sel.innerHTML = opts.join('');
    sel.value = currentTrackerId && registry[currentTrackerId] ? currentTrackerId : '';
    pigeonTrackerInitial = sel.value;
  }

  // Same writes as the Assign/Unassign tracker flow, applied to whichever
  // pigeon was just saved.
  async function applyPigeonTrackerChange(pigeonId, currentTrackerId, wantedTrackerId) {
    if (currentTrackerId && currentTrackerId !== wantedTrackerId) {
      await db.collection('trackers').doc(currentTrackerId).update({ assignedPigeonId: firebase.firestore.FieldValue.delete() });
    }
    if (wantedTrackerId) {
      const previousPigeonId = registry[wantedTrackerId] && registry[wantedTrackerId].assignedPigeonId;
      if (previousPigeonId && previousPigeonId !== pigeonId) {
        await db.collection('pigeons').doc(previousPigeonId).update({ assignedTrackerId: firebase.firestore.FieldValue.delete() });
      }
      await db.collection('pigeons').doc(pigeonId).update({ assignedTrackerId: wantedTrackerId });
      await db.collection('trackers').doc(wantedTrackerId).update({ assignedPigeonId: pigeonId });
    } else {
      await db.collection('pigeons').doc(pigeonId).update({ assignedTrackerId: firebase.firestore.FieldValue.delete() });
    }
  }

  function resetModalFields() {
    document.getElementById('db-pigeon-name').value = '';
    document.getElementById('db-pigeon-ring').value = '';
    document.getElementById('db-pigeon-dob').value = '';
    document.getElementById('db-pigeon-color').value = '';
    document.getElementById('db-pigeon-eyecolor').value = '';
    document.getElementById('db-pigeon-strain').value = '';
    document.getElementById('db-pigeon-shortdesc').value = '';
    document.getElementById('db-pigeon-photo').value = '';
    setPhotoPreview(null);
    document.getElementById('db-pigeon-sire').value = '';
    document.getElementById('db-pigeon-dam').value = '';
    document.getElementById('db-pigeon-pedigreetext').value = '';
    selectedMarkerColor = pickAutoMarkerColor();
    selectedSex = 'unknown';
    renderSegmentedGroup('db-pigeon-sex-group', selectedSex);
    selectedArchived = false;
    renderSegmentedGroup('db-pigeon-archived-group', 'active');
    editingExistingTrackerId = null;
    populatePigeonTrackerSelect(null);
    updateShortDescCount();
    setParentsCardOpen(false);
  }

  function openAddModal() {
    editingPigeonId = null;
    editingExistingPhotoURL = null;
    resetModalFields();
    populateParentSelects(null);
    document.getElementById('db-modal-title').textContent = 'Add Pigeon';
    document.getElementById('db-modal-subtitle').textContent = 'Add a new pigeon to your loft. Only the ring number is required.';
    document.getElementById('db-pigeon-save-label').textContent = 'Save Pigeon';
    modal.classList.add('open');
  }

  function openEditModal(id, p) {
    editingPigeonId = id;
    editingExistingPhotoURL = p.photoURL || null;
    document.getElementById('db-pigeon-name').value = p.name || '';
    document.getElementById('db-pigeon-ring').value = p.ringNumber || '';
    document.getElementById('db-pigeon-dob').value = p.dateOfBirth || '';
    document.getElementById('db-pigeon-color').value = p.color || '';
    document.getElementById('db-pigeon-eyecolor').value = p.eyeColor || '';
    document.getElementById('db-pigeon-strain').value = p.strainFamily || '';
    document.getElementById('db-pigeon-shortdesc').value = p.shortDescription || '';
    document.getElementById('db-pigeon-photo').value = '';
    setPhotoPreview(p.photoURL || null);
    populateParentSelects(id);
    pdiEnsureVirtualParentOption('db-pigeon-sire', p.sireId);
    pdiEnsureVirtualParentOption('db-pigeon-dam', p.damId);
    document.getElementById('db-pigeon-sire').value = p.sireId || '';
    document.getElementById('db-pigeon-dam').value = p.damId || '';
    document.getElementById('db-pigeon-pedigreetext').value = p.pedigreeText || '';
    setParentsCardOpen(!!(p.sireId || p.damId || p.pedigreeText));
    updateShortDescCount();
    editingExistingTrackerId = p.assignedTrackerId || null;
    populatePigeonTrackerSelect(editingExistingTrackerId);
    selectedMarkerColor = p.markerColor || pickAutoMarkerColor();
    selectedSex = p.sex || 'unknown';
    renderSegmentedGroup('db-pigeon-sex-group', selectedSex);
    selectedArchived = !!p.isArchived;
    renderSegmentedGroup('db-pigeon-archived-group', selectedArchived ? 'archived' : 'active');
    document.getElementById('db-modal-title').textContent = 'Edit Pigeon';
    document.getElementById('db-modal-subtitle').textContent = 'Update this pigeon\'s details. Only the ring number is required.';
    document.getElementById('db-pigeon-save-label').textContent = 'Update Pigeon';
    modal.classList.add('open');
  }

  document.getElementById('db-add-pigeon-btn').onclick = openAddModal;
  document.getElementById('db-pigeon-cancel').onclick = () => modal.classList.remove('open');

  // ---- Pedigree Import -- upload a photo/PDF of a paper pedigree; the
  // parsePedigree Cloud Function reads it with AI vision and returns every
  // bird found plus its parent links. The user reviews the list, then ALL
  // chosen birds are created in one batch with sire/dam ids already linked.
  // Anything that has no matching pigeon field (results, achievements,
  // breeder, remarks) goes into the pigeon's Pedigree Text so nothing is lost.
  // A bird whose ring number is already in this loft is never duplicated or
  // overwritten -- children just link to the existing pigeon.
  const PEDIGREE_IMPORT_URL = 'https://us-central1-flightpathlive-24331.cloudfunctions.net/parsePedigree';
  const PDI_MAX_PDF_BYTES = 6.5 * 1024 * 1024;
  let pdiPlan = null; // { rootKey, entries: [{ key, bird, existing, include, gen }], documentNotes }

  const pdiModal = document.getElementById('db-pedimport-modal');
  const pdiNorm = s => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const pdiEsc = s => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  function pdiShowStep(step) {
    ['upload', 'reading', 'review', 'error'].forEach(s => {
      document.getElementById('pdi-step-' + s).style.display = s === step ? '' : 'none';
    });
    document.getElementById('pdi-retry-btn').style.display = step === 'error' ? '' : 'none';
    document.getElementById('pdi-import-btn').style.display = step === 'review' ? '' : 'none';
  }
  function openPedigreeImportModal() {
    document.getElementById('pdi-file').value = '';
    pdiPlan = null;
    pdiShowStep('upload');
    pdiModal.classList.add('open');
  }
  function pdiFail(message) {
    document.getElementById('pdi-error-msg').textContent = message;
    pdiShowStep('error');
  }

  // Photos are shrunk before upload (phone photos are huge and the reader
  // doesn't need more than ~2200px); PDFs go as-is.
  async function pdiPrepareFile(file) {
    if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
      if (file.size > PDI_MAX_PDF_BYTES) throw new Error('That PDF is too large (over 6 MB). Try a smaller file or a photo.');
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = () => reject(new Error('Could not read that file.'));
        fr.readAsDataURL(file);
      });
      return { mediaType: 'application/pdf', data: String(dataUrl).split(',')[1] };
    }
    if (!/^image\/(jpeg|png|webp|gif)$/.test(file.type)) throw new Error('Please upload a JPG, PNG or PDF file.');
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 2200 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    return { mediaType: 'image/jpeg', data: canvas.toDataURL('image/jpeg', 0.88).split(',')[1] };
  }

  async function pdiHandleFile(file) {
    if (!file) return;
    pdiShowStep('reading');
    try {
      const prepared = await pdiPrepareFile(file);
      const idToken = await auth.currentUser.getIdToken();
      const resp = await fetch(PEDIGREE_IMPORT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + idToken },
        body: JSON.stringify(prepared)
      }).then(r => r.json());
      if (!resp.ok) { pdiFail(resp.message || 'Could not read that pedigree.'); return; }
      pdiBuildPlan(resp.result);
      if (!pdiPlan.entries.length) { pdiFail("No pigeons were found in that file. Try a clearer photo or the original PDF."); return; }
      pdiRenderReview();
      pdiShowStep('review');
    } catch (e) {
      pdiFail(e && e.message ? e.message : 'Could not read that pedigree. Please try again.');
    }
  }

  // Review model: the MAIN pigeon (always imported) + ANCESTORS (optional).
  // Ancestors the user does not tick are not made into pigeons; they are saved
  // inside the main pigeon's record (pedigreeAncestors) and resolved by
  // findPigeonDoc('anc:<mainId>:<key>'), so the pedigree chart is never missing
  // anyone. A bird whose ring already exists in this loft is linked, never duplicated.
  function pdiBuildPlan(result) {
    const own = allPigeonDocs.filter(d => d.p.orgId === currentOrg.slug);
    const ownByRing = {};
    own.forEach(d => { const k = pdiNorm(d.p.ringNumber); if (k) ownByRing[k] = d; });
    const birds = (result.birds || []).filter(b => b && b.key && (pdiNorm(b.ringNumber) || String(b.name || '').trim()));
    const byKey = {};
    birds.forEach(b => { byKey[b.key] = b; });
    const rootKey = byKey[result.rootKey] ? result.rootKey : (birds[0] && birds[0].key);
    const info = {}; // key -> { gen, relation }
    if (rootKey) {
      info[rootKey] = { gen: 0, relation: 'Main pigeon' };
      const queue = [rootKey];
      while (queue.length) {
        const k = queue.shift();
        [['sireKey', 'Sire'], ['damKey', 'Dam']].forEach(([f, label]) => {
          const pk = byKey[k][f];
          if (pk && byKey[pk] && !info[pk]) {
            info[pk] = { gen: info[k].gen + 1, relation: label + (k === rootKey ? '' : ' of ' + info[k].relation) };
            queue.push(pk);
          }
        });
      }
    }
    let vn = 0;
    const entries = birds.map(b => {
      const i = info[b.key];
      return {
        key: b.key, bird: b, existing: ownByRing[pdiNorm(b.ringNumber)] || null,
        gen: i ? i.gen : 99, relation: i ? i.relation : 'Other bird on the document',
        include: false, isMain: b.key === rootKey, vkey: null
      };
    });
    entries.sort((a, b) => a.gen - b.gen);
    entries.forEach(e => { if (!e.isMain) e.vkey = 'a' + (++vn); });
    pdiPlan = {
      rootKey, entries,
      main: entries.find(e => e.isMain) || null,
      ancestors: entries.filter(e => !e.isMain),
      documentNotes: String(result.documentNotes || '').trim()
    };
  }

  function pdiFactsHtml(b) {
    const facts = [b.color && 'Color: ' + b.color, b.eyeColor && 'Eye: ' + b.eyeColor, b.strain && 'Bloodline: ' + b.strain, b.dateOfBirth && 'Born: ' + b.dateOfBirth].filter(Boolean).map(pdiEsc).join(' · ');
    const notes = String(b.notes || '').trim();
    return (facts ? `<div class="pdi-facts">${facts}</div>` : '') +
      (notes ? `<div class="pdi-notes">${pdiEsc(notes.length > 160 ? notes.slice(0, 160) + '…' : notes)}</div>` : '');
  }
  function pdiSexTag(b) {
    return b.sex === 'cock' ? '<span class="pdi-sextag pdi-sextag--m">Cock</span>' : b.sex === 'hen' ? '<span class="pdi-sextag pdi-sextag--f">Hen</span>' : '';
  }
  function pdiRenderReview() {
    const m = pdiPlan.main;
    const mb = m.bird;
    document.getElementById('pdi-main-card').innerHTML = `
      <div class="pdi-main-top"><span class="pdi-main-badge"><i class="fa-solid fa-lock"></i> Main pigeon — always imported</span>${pdiSexTag(mb)}</div>
      <div class="pdi-row-top"><b class="pdi-ring">${pdiEsc(mb.ringNumber || 'No ring number')}</b><span class="pdi-name">${pdiEsc(mb.name || '')}</span></div>
      ${pdiFactsHtml(mb)}
      ${m.existing ? '<div class="pdi-existing">Already in your loft — this pedigree will be attached to the existing pigeon (its own details are not changed).</div>' : ''}`;
    const list = document.getElementById('pdi-list');
    list.innerHTML = pdiPlan.ancestors.map((e, i) => {
      const b = e.bird;
      return `<label class="pdi-row${e.existing ? ' pdi-row--existing' : ''}">
        <input type="checkbox" data-idx="${i}" ${e.existing ? 'checked disabled' : ''}>
        <div class="pdi-row-body">
          <div class="pdi-row-top"><b class="pdi-ring">${pdiEsc(b.ringNumber || 'No ring number')}${b.sex === 'cock' ? ' M' : b.sex === 'hen' ? ' F' : ''}</b>${b.name ? `<span class="pdi-name">“${pdiEsc(b.name)}”</span>` : ''}<span class="pdi-sextag-wrap">${pdiSexTag(b)}</span></div>
          <div class="pdi-rel">${pdiEsc(e.relation)}</div>
          ${pdiFactsHtml(b)}
          ${e.existing ? '<div class="pdi-existing">Already in your loft — linked automatically</div>' : ''}
        </div>
      </label>`;
    }).join('');
    list.querySelectorAll('input[type=checkbox]:not(:disabled)').forEach(cb => {
      cb.onchange = () => { pdiPlan.ancestors[+cb.dataset.idx].include = cb.checked; pdiUpdateCount(); };
    });
    document.getElementById('pdi-anc-body').style.display = '';
    document.getElementById('pdi-anc-card').classList.add('open');
    pdiUpdateCount();
  }
  function pdiUpdateCount() {
    const pick = pdiPlan.ancestors.filter(e => !e.existing);
    const n = pick.filter(e => e.include).length;
    document.getElementById('pdi-anc-count').textContent = n + '/' + pick.length;
    document.getElementById('pdi-anc-sub').textContent = n
      ? n + ' ancestor' + (n === 1 ? '' : 's') + ' will be added to My Loft as pigeons'
      : 'No ancestors selected — only the main pigeon will be added to My Loft';
    document.getElementById('pdi-select-all').textContent = pick.length && n === pick.length ? 'Clear' : 'Select All';
    document.getElementById('pdi-select-all').style.display = pick.length ? '' : 'none';
    document.getElementById('pdi-anc-card').style.display = pdiPlan.ancestors.length ? '' : 'none';
    const btn = document.getElementById('pdi-import-btn');
    btn.disabled = false;
    btn.innerHTML = '<i class="fa-solid fa-file-import"></i> ' + (n ? 'Import main pigeon + ' + n + ' ancestor' + (n === 1 ? '' : 's') : 'Import main pigeon');
  }

  async function pdiRunImport() {
    const m = pdiPlan.main;
    if (!m) return;
    const btn = document.getElementById('pdi-import-btn');
    btn.disabled = true;
    btn.innerHTML = '<span class="db-spinner"></span> Importing...';
    try {
      const mainRef = m.existing ? db.collection('pigeons').doc(m.existing.id) : db.collection('pigeons').doc();
      const mainId = mainRef.id;
      const realRefs = {}; // key -> ref for ancestors becoming real pigeons
      pdiPlan.ancestors.forEach(e => { if (e.include && !e.existing) realRefs[e.key] = db.collection('pigeons').doc(); });
      const idFor = key => {
        if (!key) return null;
        if (key === pdiPlan.rootKey) return mainId;
        const e = pdiPlan.ancestors.find(x => x.key === key);
        if (!e) return null;
        if (e.existing) return e.existing.id;
        if (realRefs[key]) return realRefs[key].id;
        return 'anc:' + mainId + ':' + e.vkey;
      };
      const used = new Set(allPigeonDocs.filter(d => d.p.orgId === currentOrg.slug).map(d => d.p.markerColor).filter(Boolean));
      const free = MARKER_COLOR_PALETTE.map(c => c.hex).filter(h => !used.has(h));
      let colorIdx = 0;
      const bodyOf = (e) => {
        const b = e.bird;
        const ring = String(b.ringNumber || '').trim();
        const dobOk = /^\d{4}-\d{2}-\d{2}$/.test(String(b.dateOfBirth || ''));
        const notes = [
          String(b.notes || '').trim(),
          b.breeder ? 'Breeder: ' + String(b.breeder).trim() : '',
          !dobOk && b.dateOfBirth ? 'Born: ' + String(b.dateOfBirth).trim() : ''
        ].filter(Boolean).join('\n');
        return {
          name: String(b.name || '').trim() || ring,
          ringNumber: ring,
          color: String(b.color || '').trim(),
          dateOfBirth: dobOk ? b.dateOfBirth : null,
          sex: b.sex === 'cock' || b.sex === 'hen' ? b.sex : 'unknown',
          eyeColor: String(b.eyeColor || '').trim(),
          strainFamily: String(b.strain || '').trim(),
          pedigreeText: notes,
          sireId: idFor(b.sireKey),
          damId: idFor(b.damKey)
        };
      };
      const newPigeonDoc = (e) => Object.assign(bodyOf(e), {
        photoURL: null,
        markerColor: free.length ? free[colorIdx++ % free.length] : MARKER_COLOR_PALETTE[colorIdx++ % MARKER_COLOR_PALETTE.length].hex,
        shortDescription: '',
        isArchived: false,
        orgId: currentOrg.slug,
        ownerUid: auth.currentUser.uid,
        createdAt: firebase.firestore.FieldValue.serverTimestamp()
      });
      // ancestors that stay inside the main pigeon's pedigree (not pigeons)
      const virtualMap = {};
      pdiPlan.ancestors.forEach(e => {
        if (e.existing || realRefs[e.key]) return;
        virtualMap[e.vkey] = bodyOf(e);
      });

      const ops = [];
      const mainBody = bodyOf(m);
      if (m.existing) {
        const patch = { pedigreeAncestors: virtualMap };
        if (!m.existing.p.sireId && mainBody.sireId) patch.sireId = mainBody.sireId;
        if (!m.existing.p.damId && mainBody.damId) patch.damId = mainBody.damId;
        ops.push([mainRef, patch, 'update']);
      } else {
        ops.push([mainRef, Object.assign(newPigeonDoc(m), { pedigreeAncestors: virtualMap }), 'set']);
      }
      pdiPlan.ancestors.forEach(e => { if (realRefs[e.key]) ops.push([realRefs[e.key], newPigeonDoc(e), 'set']); });

      const commits = [];
      let batch = db.batch(), inBatch = 0;
      ops.forEach(([ref, data, kind]) => {
        if (kind === 'update') batch.update(ref, data); else batch.set(ref, data);
        if (++inBatch === 400) { commits.push(batch.commit()); batch = db.batch(); inBatch = 0; }
      });
      if (inBatch) commits.push(batch.commit());
      await Promise.all(commits);
      pdiModal.classList.remove('open');
      const extra = Object.keys(realRefs).length;
      showToast('✅ Imported ' + (m.existing ? 'pedigree for ' + (m.bird.ringNumber || 'the pigeon') : 'main pigeon') + (extra ? ' + ' + extra + ' ancestor' + (extra === 1 ? '' : 's') : ''));
    } catch (e) {
      pdiFail('Could not save the pigeons: ' + (e.message || 'please try again.'));
    }
  }

  // Ancestor from an import that is NOT a pigeon in the loft: shown in dropdowns
  // when editing a pigeon that points at one, so saving never drops the link.
  function pdiEnsureVirtualParentOption(selectId, refId) {
    if (!refId || String(refId).indexOf('anc:') !== 0) return;
    const sel = document.getElementById(selectId);
    const v = findPigeonDoc(refId);
    if (!sel || !v) return;
    if ([...sel.options].some(o => o.value === refId)) return;
    const opt = document.createElement('option');
    opt.value = refId;
    opt.textContent = (v.p.ringNumber ? v.p.ringNumber + ' (' + v.p.name + ')' : v.p.name);
    sel.appendChild(opt);
  }

  document.getElementById('pedigree-import-btn').onclick = openPedigreeImportModal;
  document.getElementById('pdi-cancel-btn').onclick = () => pdiModal.classList.remove('open');
  document.getElementById('pdi-retry-btn').onclick = () => pdiShowStep('upload');
  document.getElementById('pdi-import-btn').onclick = pdiRunImport;
  document.getElementById('pdi-select-all').onclick = () => {
    const pick = pdiPlan.ancestors.filter(e => !e.existing);
    const all = pick.every(e => e.include);
    pick.forEach(e => { e.include = !all; });
    document.querySelectorAll('#pdi-list input[type=checkbox]:not(:disabled)').forEach(cb => { cb.checked = !all; });
    pdiUpdateCount();
  };
  document.getElementById('pdi-anc-toggle').onclick = () => {
    const body = document.getElementById('pdi-anc-body');
    const open = body.style.display === 'none';
    body.style.display = open ? '' : 'none';
    document.getElementById('pdi-anc-card').classList.toggle('open', open);
  };
  (() => {
    const zone = document.getElementById('pdi-drop');
    const input = document.getElementById('pdi-file');
    zone.onclick = () => input.click();
    input.onchange = () => pdiHandleFile(input.files[0]);
    ['dragenter', 'dragover'].forEach(ev => zone.addEventListener(ev, e => { e.preventDefault(); zone.classList.add('dragover'); }));
    ['dragleave', 'drop'].forEach(ev => zone.addEventListener(ev, e => { e.preventDefault(); zone.classList.remove('dragover'); }));
    zone.addEventListener('drop', e => { if (e.dataTransfer.files && e.dataTransfer.files[0]) pdiHandleFile(e.dataTransfer.files[0]); });
  })();

  document.getElementById('db-pigeon-save').onclick = async () => {
    const nameInput = document.getElementById('db-pigeon-name').value.trim();
    const ring = document.getElementById('db-pigeon-ring').value.trim();
    const dateOfBirth = document.getElementById('db-pigeon-dob').value || null;
    const color = document.getElementById('db-pigeon-color').value.trim();
    const eyeColor = document.getElementById('db-pigeon-eyecolor').value.trim();
    const strainFamily = document.getElementById('db-pigeon-strain').value.trim();
    const shortDescription = document.getElementById('db-pigeon-shortdesc').value.trim();
    const sireId = document.getElementById('db-pigeon-sire').value || null;
    const damId = document.getElementById('db-pigeon-dam').value || null;
    const pedigreeText = document.getElementById('db-pigeon-pedigreetext').value.trim();
    const photoFile = document.getElementById('db-pigeon-photo').files[0];
    const statusEl = document.getElementById('db-pigeon-status');

    // Only the ring number is required. A blank Name falls back to the ring
    // number so a bird is never nameless in lists, dropdowns or search.
    if (!ring) {
      statusEl.textContent = 'Ring number is required.';
      statusEl.style.color = '#c0392b';
      return;
    }
    const name = nameInput || ring;

    statusEl.innerHTML = '<span class="db-spinner"></span> Saving...';
    statusEl.style.color = '#6b7a87';

    try {
      let photoURL = editingExistingPhotoURL;
      if (photoFile) {
        const path = 'pigeons/' + currentOrg.slug + '/' + Date.now() + '_' + photoFile.name;
        const storageInstance = await getStorage();
        const ref = storageInstance.ref().child(path);
        await ref.put(photoFile);
        photoURL = await ref.getDownloadURL();
      }

      let savedPigeonId = editingPigeonId;
      if (editingPigeonId) {
        await db.collection('pigeons').doc(editingPigeonId).update({
          name, ringNumber: ring, color, photoURL, markerColor: selectedMarkerColor,
          dateOfBirth, sex: selectedSex, eyeColor, strainFamily, shortDescription, sireId, damId, pedigreeText,
          isArchived: selectedArchived,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp()
        });
      } else {
        const newPigeonRef = await db.collection('pigeons').add({
          name, ringNumber: ring, color, photoURL, markerColor: selectedMarkerColor,
          dateOfBirth, sex: selectedSex, eyeColor, strainFamily, shortDescription, sireId, damId, pedigreeText,
          isArchived: selectedArchived,
          orgId: currentOrg.slug,
          ownerUid: auth.currentUser.uid,
          createdAt: firebase.firestore.FieldValue.serverTimestamp()
        });
        savedPigeonId = newPigeonRef.id;
      }

      // Optional tracker -- only touched if the dropdown was actually changed.
      const trackerSel = document.getElementById('db-pigeon-tracker');
      if (trackerSel && trackerSel.value !== pigeonTrackerInitial) {
        await applyPigeonTrackerChange(savedPigeonId, editingExistingTrackerId, trackerSel.value || null);
      }

      // Pigeons and trackers are independent now -- Ring Number is just the
      // bird's real racing ring, no Vernal call happens here at all. Add a
      // tracker separately from the Trackers tab, then assign it.
      statusEl.textContent = 'Saved!';
      statusEl.style.color = '#2e8b57';
      showToast(editingPigeonId ? '✅ Pigeon updated' : '✅ Pigeon saved');
      setTimeout(() => {
        modal.classList.remove('open');
        statusEl.textContent = '';
        resetModalFields();
        editingPigeonId = null;
        editingExistingPhotoURL = null;
        editingExistingTrackerId = null;
      }, 700);
    } catch (e) {
      statusEl.textContent = e.message;
      statusEl.style.color = '#c0392b';
    }
  };

  // Before a pigeon is deleted, every bird that points at it as a parent (and every imported
  // ancestor record that does) is given its own copy of that pigeon's details -- stored as an
  // ancestor record inside the descendant, exactly like an imported pedigree ancestor -- and its
  // links are repointed to that copy. So the pedigree of the offspring stays complete; the deleted
  // bird just stops being a pigeon in the list. Writes go into the caller's batch.
  function preserveDeletedPigeonInPedigrees(id, batch) {
    const P = allPigeonDocs.find(d => d.id === id);
    if (!P) return 0;
    const tag = 'p' + String(id).slice(0, 6);
    const fromP = ref => ref === id || (ref && String(ref).indexOf('anc:' + id + ':') === 0);
    let touched = 0;
    allPigeonDocs.forEach(D => {
      if (D.id === id) return;
      const anc = D.p.pedigreeAncestors || {};
      const refsTop = ['sireId', 'damId'].filter(f => fromP(D.p[f]));
      const refsInside = Object.keys(anc).filter(k => anc[k] && (fromP(anc[k].sireId) || fromP(anc[k].damId)));
      if (!refsTop.length && !refsInside.length) return;
      const newRef = ref => ref === id ? 'anc:' + D.id + ':' + tag
        : (ref && String(ref).indexOf('anc:' + id + ':') === 0 ? 'anc:' + D.id + ':' + tag + '_' + String(ref).split(':')[2] : ref);
      const map = Object.assign({}, anc);
      // the deleted pigeon's own imported ancestors come along, with their links rewritten
      const pAnc = P.p.pedigreeAncestors || {};
      Object.keys(pAnc).forEach(k => { map[tag + '_' + k] = Object.assign({}, pAnc[k], { sireId: newRef(pAnc[k].sireId) || '', damId: newRef(pAnc[k].damId) || '' }); });
      // ...and the deleted pigeon itself, as an ancestor record
      map[tag] = {
        name: P.p.name || '', ringNumber: P.p.ringNumber || '', sex: P.p.sex || 'unknown', color: P.p.color || '',
        eyeColor: P.p.eyeColor || '', strainFamily: P.p.strainFamily || '', dateOfBirth: P.p.dateOfBirth || null,
        pedigreeText: P.p.pedigreeText || '', sireId: newRef(P.p.sireId) || '', damId: newRef(P.p.damId) || ''
      };
      refsInside.forEach(k => {
        map[k] = Object.assign({}, map[k], { sireId: newRef(anc[k].sireId) || '', damId: newRef(anc[k].damId) || '' });
      });
      const patch = { pedigreeAncestors: map };
      refsTop.forEach(f => { patch[f] = newRef(D.p[f]); });
      batch.update(db.collection('pigeons').doc(D.id), patch);
      touched++;
    });
    return touched;
  }

  async function deletePigeon(id, name, assignedTrackerId) {
    if (!(await showConfirmModal(
      'This cannot be undone — "' + name + '" is removed from your loft for good. Its offspring keep its details in their pedigrees. If it is just lost, dead, or sold, mark it Inactive instead (Edit Pigeon → Status) to keep it in your list.',
      'Delete pigeon permanently?',
      'Delete Forever'
    ))) return;
    try {
      // Deleting the pigeon alone left its tracker (if any) with a stale
      // assignedPigeonId pointing at a doc that no longer exists -- the
      // tracker card would still fall back to showing "Unassigned" (since
      // the lookup fails), but the field itself stuck around forever,
      // silently wrong. Batch both writes so they succeed or fail together.
      const batch = db.batch();
      preserveDeletedPigeonInPedigrees(id, batch); // offspring keep their pedigree
      batch.delete(db.collection('pigeons').doc(id));
      if (assignedTrackerId) {
        batch.update(db.collection('trackers').doc(assignedTrackerId), {
          assignedPigeonId: firebase.firestore.FieldValue.delete(),
        });
      }
      await batch.commit();
    } catch (e) {
      alert('Could not delete: ' + e.message);
    }
  }

  // ==== PIGEON PEDIGREE PAGE ====
  // A full dedicated page (its own .db-panel, no sidebar entry of its own --
  // same "reachable only by drilling in" pattern as New Track) showing up to
  // 4 generations of a pigeon's family tree, built purely from the
  // sireId/damId links set on the Add/Edit Pigeon form. Clicking any real
  // ancestor re-centers the whole page on that bird (it becomes the new
  // Generation 0), so the same view doubles as a way to browse an entire
  // family line. See activateTab's 'pigeon-pedigree' case for the entry
  // point into renderPedigreePage().

  function findPigeonDoc(id) {
    if (!id) return null;
    // 'anc:<mainPigeonId>:<key>' = an ancestor saved inside a pigeon's imported pedigree
    // (not a pigeon in the loft) -- looks like a pigeon doc everywhere the pedigree reads it.
    if (typeof id === 'string' && id.indexOf('anc:') === 0) {
      const parts = id.split(':');
      const main = allPigeonDocs.find(d => d.id === parts[1]);
      const a = main && main.p.pedigreeAncestors && main.p.pedigreeAncestors[parts[2]];
      return a ? { id, p: Object.assign({}, a, { orgId: main.p.orgId, isArchived: false, isImportedAncestor: true }) } : null;
    }
    return allPigeonDocs.find(d => d.id === id);
  }
  function pigeonOffspringOf(id) {
    return allPigeonDocs.filter(d => d.p.sireId === id || d.p.damId === id)
      .sort((a, b) => (new Date(b.p.dateOfBirth || 0).getTime() || 0) - (new Date(a.p.dateOfBirth || 0).getTime() || 0));
  }
  // Full siblings only -- same sire AND same dam as the subject, and only
  // once the subject has at least one parent recorded (otherwise every
  // other parentless bird in the loft would "match" on two blank fields).
  function pigeonSiblingsOf(id, p) {
    if (!p.sireId && !p.damId) return [];
    return allPigeonDocs.filter(d => d.id !== id && d.p.sireId === p.sireId && d.p.damId === p.damId)
      .sort((a, b) => (new Date(b.p.dateOfBirth || 0).getTime() || 0) - (new Date(a.p.dateOfBirth || 0).getTime() || 0));
  }
  function pigeonTrainingRowsOf(id) {
    return Object.keys(trainings)
      .filter(tid => (trainings[tid].participatingPigeonIds || []).includes(id))
      .map(tid => trainings[tid])
      .sort((a, b) => (b.releaseAtMs || 0) - (a.releaseAtMs || 0));
  }
  // Arrival order among the birds that actually came home from that one
  // training doubles as its finishing order -- there's no separate "race
  // placement" field anywhere, but first-timestamp-back already means
  // first place for anyone racing the same release.
  function pigeonRankInTraining(t, id) {
    const arrivals = t.arrivals || {};
    if (!arrivals[id]) return null;
    const arrivedIds = (t.participatingPigeonIds || []).filter(pid => arrivals[pid]);
    arrivedIds.sort((a, b) => arrivals[a].arrivedAtMs - arrivals[b].arrivedAtMs);
    return arrivedIds.indexOf(id) + 1;
  }
  function pigeonPerformanceStats(id) {
    let participated = 0, firsts = 0, top10 = 0, speedSum = 0, speedCount = 0;
    Object.values(trainings).forEach(t => {
      if (trainingStatusOf(t) !== 'completed') return;
      if (!(t.participatingPigeonIds || []).includes(id)) return;
      participated++;
      const a = (t.arrivals || {})[id];
      if (!a) return;
      const rank = pigeonRankInTraining(t, id);
      if (rank === 1) firsts++;
      if (rank && rank <= 10) top10++;
      if (a.speedKmH) { speedSum += a.speedKmH; speedCount++; }
    });
    return { participated, firsts, top10, avgSpeedMPerMin: speedCount ? speedToMPerMin(speedSum / speedCount) : null };
  }

  // ---- Tree geometry -- one shared layout computation consumed by both the
  // on-screen DOM/SVG tree and the canvas drawn for PDF export, so the two
  // can never drift apart. Columns run outward from the center in both
  // directions: Sire's own ancestry occupies the columns to the left
  // (immediate Sire closest to center), Dam's the mirror image to the
  // right -- the classic pedigree-chart shape, not a single trunk growing
  // in one direction.
  const PDG_BOX_W = 168, PDG_BOX_H = 60, PDG_COL_GAP = 46, PDG_ROW_GAP = 14, PDG_PADDING = 24, PDG_LABEL_ROW_H = 40;
  const PDG_LINE_COLOR = { sire: '#2f6fed', dam: '#d6336c' };
  function pdgGenLabel(n) { return n === 1 ? 'Parents' : n === 2 ? 'Grandparents' : 'Great-Grandparents'; }

  function computePedigreeLayout(subjectId, subjectP, maxGen) {
    const boxW = PDG_BOX_W, boxH = PDG_BOX_H, colGap = PDG_COL_GAP, rowGap = PDG_ROW_GAP, padding = PDG_PADDING;

    // One side's ancestor columns, centered the classic way: each slot's Y
    // is the average of its own two children's Y -- exactly how a real
    // printed pedigree chart keeps a parent vertically between its two
    // forebears, recursed all the way down from the outermost generation.
    function buildSide(startDoc) {
      const cols = [[startDoc]];
      for (let k = 1; k < maxGen; k++) {
        const prev = cols[k - 1];
        const cur = [];
        prev.forEach(doc => {
          cur.push(doc ? findPigeonDoc(doc.p.sireId) : null);
          cur.push(doc ? findPigeonDoc(doc.p.damId) : null);
        });
        cols.push(cur);
      }
      const last = maxGen - 1;
      const colY = [];
      colY[last] = cols[last].map((_, i) => i * (boxH + rowGap));
      for (let k = last - 1; k >= 0; k--) {
        colY[k] = cols[k].map((_, i) => (colY[k + 1][i * 2] + colY[k + 1][i * 2 + 1]) / 2);
      }
      return { cols, colY };
    }

    const sireSide = buildSide(findPigeonDoc(subjectP.sireId));
    const damSide = buildSide(findPigeonDoc(subjectP.damId));
    const rootY = sireSide.colY[0][0]; // === damSide.colY[0][0] -- both sides are always the same depth
    const leafCount = Math.pow(2, maxGen - 1);
    const treeHeight = leafCount * boxH + (leafCount - 1) * rowGap;

    const xForSireCol = k => padding + (maxGen - 1 - k) * (boxW + colGap);
    const xForDamCol = k => padding + (maxGen + 1 + k) * (boxW + colGap);
    const xForCurrent = padding + maxGen * (boxW + colGap);

    const positions = {};
    positions.self = { key: 'self', doc: { id: subjectId, p: subjectP }, x: xForCurrent, y: rootY, side: 'self' };
    ['sire', 'dam'].forEach(sideName => {
      const side = sideName === 'sire' ? sireSide : damSide;
      const xForCol = sideName === 'sire' ? xForSireCol : xForDamCol;
      for (let k = 0; k < maxGen; k++) {
        side.cols[k].forEach((doc, i) => {
          const key = sideName + '-' + k + '-' + i;
          // Only the immediate Sire/Dam (k===0) get their side's tint --
          // grandparents and beyond stay neutral per the spec, with just the
          // connecting lines carrying the blue/pink all the way out.
          positions[key] = { key, doc, x: xForCol(k), y: side.colY[k][i], side: k === 0 ? sideName : 'neutral' };
        });
      }
    });

    const connectors = [{ from: 'self', to: 'sire-0-0', side: 'sire' }, { from: 'self', to: 'dam-0-0', side: 'dam' }];
    ['sire', 'dam'].forEach(sideName => {
      for (let k = 1; k < maxGen; k++) {
        const count = Math.pow(2, k);
        for (let i = 0; i < count; i++) {
          connectors.push({ from: sideName + '-' + (k - 1) + '-' + Math.floor(i / 2), to: sideName + '-' + k + '-' + i, side: sideName });
        }
      }
    });

    const labels = [];
    for (let k = maxGen - 1; k >= 0; k--) labels.push({ x: xForSireCol(k), gen: k + 1, text: pdgGenLabel(k + 1) });
    labels.push({ x: xForCurrent, gen: 0, text: 'Current Pigeon' });
    for (let k = 0; k < maxGen; k++) labels.push({ x: xForDamCol(k), gen: k + 1, text: pdgGenLabel(k + 1) });

    const totalWidth = padding * 2 + (2 * maxGen + 1) * boxW + 2 * maxGen * colGap;
    const totalHeight = treeHeight + padding * 2 + PDG_LABEL_ROW_H;
    return { positions, connectors, labels, totalWidth, totalHeight, boxW, boxH, padding, labelRowH: PDG_LABEL_ROW_H };
  }

  // Same FontAwesome mars/venus icons (and the same blue/pink) as Pigeon
  // List's own cards use for sex, so Pedigree reads as the same visual
  // language rather than inventing its own symbol style. Unknown sex
  // deliberately shows nothing, same as every other sex-aware spot in the
  // app when sex hasn't been set.
  function pdgSexSymbolHtml(sex) {
    if (sex === 'cock') return '<i class="fa-solid fa-mars pdg-sex-symbol pdg-sex-symbol--male"></i>';
    if (sex === 'hen') return '<i class="fa-solid fa-venus pdg-sex-symbol pdg-sex-symbol--female"></i>';
    return '';
  }
  function pdgBoxHtml(doc, side) {
    if (!doc) return `<div class="pdg-box pdg-box--empty"><span>Not recorded</span></div>`;
    const cls = 'pdg-box pdg-box--clickable' + (side === 'self' ? ' pdg-box--self' : side === 'sire' ? ' pdg-box--sire' : side === 'dam' ? ' pdg-box--dam' : '');
    return `<div class="${cls}" data-pigeon-id="${doc.id}">
      <div class="pdg-box-photo">${pigeonAvatarHtml(doc.p)}</div>
      <div class="pdg-box-body">
        <div class="pdg-box-name">${doc.p.name}</div>
        <div class="pdg-box-meta">${pdgSexSymbolHtml(doc.p.sex)}${doc.p.ringNumber ? '<span class="pdg-box-ring">' + doc.p.ringNumber + '</span>' : ''}</div>
      </div>
    </div>`;
  }

  function renderPedigreeTreeDesktopHtml(layout) {
    const { positions, connectors, labels, totalWidth, totalHeight, boxW, boxH, padding, labelRowH } = layout;
    const topOffset = padding + labelRowH;
    let labelsHtml = '';
    labels.forEach(l => {
      labelsHtml += `<div class="pdg-gen-label" style="left:${l.x}px;width:${boxW}px;">
        <div class="pdg-gen-label-num">${l.gen === 0 ? 'Current Pigeon' : 'Generation ' + l.gen}</div>
        <div class="pdg-gen-label-text">${l.gen === 0 ? '' : l.text}</div>
      </div>`;
    });
    let boxesHtml = '';
    Object.values(positions).forEach(pos => {
      boxesHtml += `<div class="pdg-abs-box" style="left:${pos.x}px;top:${pos.y + topOffset}px;width:${boxW}px;height:${boxH}px;">${pdgBoxHtml(pos.doc, pos.side)}</div>`;
    });
    let paths = '';
    connectors.forEach(c => {
      const a = positions[c.from], b = positions[c.to];
      if (!a || !b) return;
      const fromLeft = a.x < b.x;
      const ax = fromLeft ? a.x + boxW : a.x, ay = a.y + boxH / 2 + topOffset;
      const bx = fromLeft ? b.x : b.x + boxW, by = b.y + boxH / 2 + topOffset;
      const midX = (ax + bx) / 2;
      paths += `<path d="M ${ax} ${ay} C ${midX} ${ay}, ${midX} ${by}, ${bx} ${by}" fill="none" stroke="${PDG_LINE_COLOR[c.side]}" stroke-width="2" opacity="0.6"/>`;
    });
    return `<div class="pdg-tree-canvas" style="width:${totalWidth}px;height:${totalHeight}px;">
      ${labelsHtml}
      <svg class="pdg-tree-svg" width="${totalWidth}" height="${totalHeight}">${paths}</svg>
      ${boxesHtml}
    </div>`;
  }

  // ---- Certificate-style pedigree (desktop + PDF): the selected pigeon sits
  // at the LEFT, its parents are the next column, then grandparents and so
  // on, each column doubling in box count and each box spanning exactly the
  // height of its own two parents -- the same shape as a printed breeder's
  // pedigree. Sire (father) line is always the upper half of every branch,
  // Dam (mother) the lower half. Built from stored sireId/damId links only,
  // so a pigeon that appears in several branches simply shows up in each.
  const PDG_SIRE_TINTS = ['#dbe8fb', '#fdf6c4', '#d8f5c9', '#c7f3e3', '#e4dcfa'];
  const PDG_DAM_TINTS  = ['#fbdde8', '#fddcbc', '#c9f2dd', '#fbd4d4', '#f1d9f0'];
  function pdgRelationLabel(gen, isSire) {
    if (gen === 0) return 'Selected pigeon';
    const base = gen === 1 ? (isSire ? 'Father' : 'Mother') : (isSire ? 'Grandfather' : 'Grandmother');
    return gen <= 2 ? base : 'Great-'.repeat(gen - 2) + base.toLowerCase();
  }
  // Everything a pedigree box can show, in one place so the screen version
  // and the PDF stay identical.
  function pdgCertFacts(doc, gen, isSire) {
    const p = doc.p;
    const facts = [];
    if (p.color) facts.push('Color: ' + p.color);
    if (p.strainFamily) facts.push('Bloodline: ' + p.strainFamily);
    if (p.breeder) facts.push('Breeder: ' + p.breeder);
    return {
      ring: p.ringNumber || 'No ring no.',
      sex: p.sex === 'cock' ? 'M' : p.sex === 'hen' ? 'F' : '',
      name: pdiNorm(p.name) && pdiNorm(p.name) === pdiNorm(p.ringNumber) ? '' : (p.name || ''),
      relation: pdgRelationLabel(gen, isSire),
      facts,
      notes: (p.pedigreeText || '').trim()
    };
  }
  // ---- Pedigree chart geometry (shared by the screen chart and the PDF), measured
  // from the printed reference pedigree: every box is PDG_G.W wide; the last
  // column is stacked sibling pairs (a leaf is PDG_G.LEAF_H tall); the column
  // before it is one frame per pair; every box further left is centred between
  // its two parents' boxes and capped at PDG_G.MAXH tall (so nothing stretches
  // into a giant box); the first parents sit slightly left, above and below
  // the selected pigeon's box, when there is room. Units are PDF points.
  const PDG_G = { W: 119, GAP: 10, LEAF_H: 39, LEAF_GAP: 2, PAIR_GAP: 10, MAXH: 165, LINE: 10.4, PADX: 5, PADY: 3, SHIFT: 38, SHADOW: 4 };
  function pdgCertNodes(subjectId, subjectP, totalGens) {
    const nodes = [];
    let level = [{ doc: { id: subjectId, p: subjectP }, isSire: true }];
    for (let gen = 0; gen < totalGens; gen++) {
      const next = [];
      level.forEach((n, idx) => {
        nodes.push({ gen, idx, doc: n.doc, isSire: n.isSire });
        if (gen < totalGens - 1) {
          next.push({ doc: n.doc ? findPigeonDoc(n.doc.p.sireId) : null, isSire: true });
          next.push({ doc: n.doc ? findPigeonDoc(n.doc.p.damId) : null, isSire: false });
        }
      });
      level = next;
    }
    return nodes;
  }
  // How many generations actually have data: an outer column that is completely
  // empty is dropped (never fewer than 3 columns, never more than requested).
  function pdgEffectiveGens(subjectId, subjectP, requested) {
    const nodes = pdgCertNodes(subjectId, subjectP, requested);
    let deepest = 0;
    nodes.forEach(n => { if (n.doc && n.gen > deepest) deepest = n.gen; });
    return Math.max(Math.min(3, requested), Math.min(requested, deepest + 1));
  }
  function pdgCertGeometry(totalGens) {
    const G = PDG_G;
    // 6+ generations: shorter end boxes, taller inner boxes -> a tighter page
    const big = totalGens >= 6;
    const small = totalGens <= 4; // few generations: roomy leaf boxes, like that column in a 5-generation chart
    const leafH = big ? 32 : small ? 80 : G.LEAF_H, leafGap = small ? 10 : G.LEAF_GAP, pairGap = big ? 8 : G.PAIR_GAP, maxH = big ? 225 : G.MAXH;
    const geo = {}; // 'gen:idx' -> { x, y, h }
    const last = totalGens - 1;
    const leaves = Math.pow(2, last);
    let y = 0;
    for (let i = 0; i < leaves; i++) {
      if (i > 0) y += (i % 2 === 0) ? pairGap : leafGap;
      geo[last + ':' + i] = { y, h: leafH };
      y += leafH;
    }
    for (let g = last - 1; g >= 0; g--) {
      for (let i = 0; i < Math.pow(2, g); i++) {
        const c1 = geo[(g + 1) + ':' + (2 * i)], c2 = geo[(g + 1) + ':' + (2 * i + 1)];
        const top = c1.y, extent = c2.y + c2.h - c1.y;
        const h = g === last - 1 && !small ? extent : Math.min(extent, maxH);
        geo[g + ':' + i] = { y: top + (extent - h) / 2, h };
      }
    }
    // columns: the first parents tuck in beside the selected pigeon's box when they clear it
    let gen1X = G.W + G.GAP, shifted = false;
    if (totalGens > 1) {
      const root = geo['0:0'], s = geo['1:0'], d = geo['1:1'];
      if (root.y > s.y + s.h + 4 && root.y + root.h < d.y - 4) { gen1X = G.SHIFT; shifted = true; }
    }
    const xFor = g => g === 0 ? 0 : gen1X + (g - 1) * (G.W + G.GAP);
    let minY = Infinity, maxY = 0;
    Object.keys(geo).forEach(k => { const g = +k.split(':')[0]; geo[k].x = xFor(g); minY = Math.min(minY, geo[k].y); maxY = Math.max(maxY, geo[k].y + geo[k].h); });
    Object.keys(geo).forEach(k => { geo[k].y -= minY; });
    // thin connector lines between each bird and its two parents: [x1, y1, x2, y2]
    const lines = [];
    const cy = b => b.y + b.h / 2;
    for (let g = 0; g < last; g++) {
      for (let i = 0; i < Math.pow(2, g); i++) {
        const c = geo[g + ':' + i], p1 = geo[(g + 1) + ':' + (2 * i)], p2 = geo[(g + 1) + ':' + (2 * i + 1)];
        if (g === 0 && shifted) {
          const vx = p1.x - 10; // parents tuck beside the child: come out of their left edges, join at the child's top / bottom
          lines.push([p1.x, cy(p1), vx, cy(p1)], [vx, cy(p1), vx, c.y]);
          lines.push([p2.x, cy(p2), vx, cy(p2)], [vx, cy(p2), vx, c.y + c.h]);
        } else {
          const mid = p1.x - G.GAP / 2;
          lines.push([c.x + G.W, cy(c), mid, cy(c)], [mid, cy(p1), mid, cy(p2)], [mid, cy(p1), p1.x, cy(p1)], [mid, cy(p2), p2.x, cy(p2)]);
        }
      }
    }
    return { geo, lines, shifted, width: xFor(last) + G.W + G.SHADOW, height: maxY - minY + G.SHADOW };
  }

  // Screen: absolutely positioned boxes from the same geometry (1pt = 4/3 px).
  const PDG_PX = 4 / 3;
  function pdgBoxLines(h) { // body lines that fit a box of height h (pt), below the ring line
    return Math.max(0, Math.floor((h - PDG_G.PADY * 2 - PDG_G.LINE) / PDG_G.LINE));
  }
  function renderPedigreeCertHtml(subjectId, subjectP, requestedGens) {
    const totalGens = pdgEffectiveGens(subjectId, subjectP, requestedGens);
    const nodes = pdgCertNodes(subjectId, subjectP, totalGens);
    const { geo, lines, width, height } = pdgCertGeometry(totalGens);
    const svg = '<svg class="pdg2-lines" width="' + Math.ceil(width * PDG_PX + 12) + '" height="' + Math.ceil(height * PDG_PX + 12) + '"><path d="' +
      lines.map(l => 'M' + (l[0] * PDG_PX).toFixed(1) + ' ' + (l[1] * PDG_PX).toFixed(1) + 'L' + (l[2] * PDG_PX).toFixed(1) + ' ' + (l[3] * PDG_PX).toFixed(1)).join('') +
      '" fill="none" stroke="#8c8c8c" stroke-width="1.1"/></svg>';
    const esc = t => String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;');
    const boxes = nodes.map(n => {
      const g = geo[n.gen + ':' + n.idx];
      const pos = 'left:' + (g.x * PDG_PX).toFixed(1) + 'px;top:' + (g.y * PDG_PX).toFixed(1) + 'px;width:' + (PDG_G.W * PDG_PX).toFixed(1) + 'px;height:' + (g.h * PDG_PX).toFixed(1) + 'px;';
      if (!n.doc) return '<div class="pdg2-box pdg2-box--empty" style="' + pos + '"></div>';
      const f = pdgCertFacts(n.doc, n.gen, n.isSire);
      const text = [f.name].concat(f.facts, [f.notes]).filter(Boolean).map(esc).join('\n');
      const lines = pdgBoxLines(g.h);
      return '<div class="pdg2-box pdg2-box--clickable" style="' + pos + '" data-pigeon-id="' + n.doc.id + '">' +
        '<div class="pdg2-head"><span class="pdg2-ring">' + esc(f.ring) + (f.sex ? ' ' + f.sex : '') + '</span></div>' +
        (lines ? '<div class="pdg2-text" style="-webkit-line-clamp:' + lines + ';">' + text + '</div>' : '') +
        '</div>';
    }).join('');
    const trimmed = totalGens < requestedGens ? '<div class="pdg2-note">No ancestors recorded beyond ' + totalGens + ' generations, so the empty outer columns are hidden.</div>' : '';
    return '<div class="pdg2-tree" style="width:' + Math.ceil(width * PDG_PX + 12) + 'px;height:' + Math.ceil(height * PDG_PX + 12) + 'px;">' + svg + boxes + '</div>' + trimmed;
  }

  // ---- Mobile tree -- a vertical Sire/Current/Dam stack (never the
  // absolute-positioned desktop grid, which would just be tiny and unreadable
  // squeezed into a phone width) with each side's deeper ancestors tucked
  // behind an expandable toggle instead of all being shown at once.
  function pdgMiniCardHtml(doc, tier) {
    if (!doc) return `<div class="pdg-mini pdg-mini--empty">Not recorded</div>`;
    return `<div class="pdg-mini pdg-mini--${tier} pdg-mini--clickable" data-pigeon-id="${doc.id}">
      <div class="pdg-mini-photo">${pigeonAvatarHtml(doc.p)}</div>
      <div class="pdg-mini-name">${doc.p.name} ${pdgSexSymbolHtml(doc.p.sex)}</div>
      ${doc.p.ringNumber ? `<div class="pdg-mini-ring">${doc.p.ringNumber}</div>` : ''}
    </div>`;
  }
  function renderPedigreeMobileHtml(subjectId, subjectP, maxGen) {
    const sire = findPigeonDoc(subjectP.sireId);
    const dam = findPigeonDoc(subjectP.damId);
    function greatGrandRow(doc, side, uid) {
      if (maxGen < 3 || !doc) return '';
      const ggs = findPigeonDoc(doc.p.sireId), ggd = findPigeonDoc(doc.p.damId);
      return `<button type="button" class="pdg-expand-toggle pdg-expand-toggle--sm pdg-expand-toggle--${side}" data-target="${uid}"><i class="fa-solid fa-chevron-down"></i></button>
        <div class="pdg-expand-row pdg-expand-row--sm" id="${uid}" style="display:none;">
          ${pdgMiniCardHtml(ggs, 'neutral')}${pdgMiniCardHtml(ggd, 'neutral')}
        </div>`;
    }
    function grandparentRow(doc, side, uid) {
      if (maxGen < 2 || !doc) return '';
      const gs = findPigeonDoc(doc.p.sireId), gd = findPigeonDoc(doc.p.damId);
      return `<button type="button" class="pdg-expand-toggle pdg-expand-toggle--${side}" data-target="${uid}"><i class="fa-solid fa-chevron-down"></i> ${doc.p.name}'s Parents</button>
        <div class="pdg-expand-row" id="${uid}" style="display:none;">
          <div class="pdg-expand-col">${pdgMiniCardHtml(gs, 'neutral')}${greatGrandRow(gs, side, uid + '-s')}</div>
          <div class="pdg-expand-col">${pdgMiniCardHtml(gd, 'neutral')}${greatGrandRow(gd, side, uid + '-d')}</div>
        </div>`;
    }
    return `<div class="pdg-mobile-tree">
      <div class="pdg-line-badge pdg-line-badge--sire">Sire Line</div>
      <div class="pdg-mobile-parent">
        ${pdgMiniCardHtml(sire, 'sire')}
        ${grandparentRow(sire, 'sire', 'pdg-exp-sire')}
      </div>
      <div class="pdg-mobile-self">${pdgBoxHtml({ id: subjectId, p: subjectP }, 'self')}</div>
      <div class="pdg-mobile-parent">
        ${pdgMiniCardHtml(dam, 'dam')}
        ${grandparentRow(dam, 'dam', 'pdg-exp-dam')}
      </div>
      <div class="pdg-line-badge pdg-line-badge--dam">Dam Line</div>
    </div>`;
  }

  // ---- Shared click-to-recenter -- any element carrying data-pigeon-id,
  // in either tree or the list, jumps the whole page to that bird.
  function wirePedigreeClickableBoxes(container) {
    if (!container) return;
    container.querySelectorAll('[data-pigeon-id]').forEach(el => {
      el.onclick = () => {
        const doc = findPigeonDoc(el.dataset.pigeonId);
        if (!doc || doc.id === pedigreeSubject.id) return;
        pedigreeSubject = { id: doc.id, p: doc.p };
        if (container.closest('#db-panel-pigeon-details')) renderPigeonDetailsPage(); else renderPedigreePage();
        const contentEl = document.querySelector('.db-content');
        if (contentEl) contentEl.scrollTop = 0;
      };
    });
  }
  function wirePedigreeExpandToggles(container) {
    if (!container) return;
    container.querySelectorAll('.pdg-expand-toggle').forEach(btn => {
      btn.onclick = (e) => {
        e.stopPropagation();
        const target = document.getElementById(btn.dataset.target);
        if (!target) return;
        const willOpen = target.style.display === 'none';
        target.style.display = willOpen ? 'flex' : 'none';
        btn.classList.toggle('pdg-expand-toggle--open', willOpen);
      };
    });
  }

  // ---- Current Pigeon info card + Performance Summary.
  function renderPedigreeInfoCard(id, p) {
    document.getElementById('pdg-info-photo').innerHTML = pigeonAvatarHtml(p);
    document.getElementById('pdg-info-name').textContent = p.name;
    document.getElementById('pdg-info-ring-badge').textContent = p.ringNumber || 'No ring number';
    document.getElementById('pdg-info-sex-badge').innerHTML = p.sex === 'cock' ? '<i class="fa-solid fa-mars"></i> Cock' : p.sex === 'hen' ? '<i class="fa-solid fa-venus"></i> Hen' : 'Sex unknown';
    const statusBadge = document.getElementById('pdg-info-status-badge');
    statusBadge.textContent = p.isArchived ? 'Inactive' : 'Active';
    statusBadge.className = 'pgn-badge ' + (p.isArchived ? 'pgn-badge--inactive' : 'pgn-badge--active');
    document.getElementById('pdg-info-ring').textContent = p.ringNumber || '—';
    document.getElementById('pdg-info-dob').textContent = p.dateOfBirth ? fmtDateOnly(new Date(p.dateOfBirth + 'T00:00:00').getTime()) : '—';
    document.getElementById('pdg-info-color').textContent = p.color || '—';
    document.getElementById('pdg-info-breed').textContent = p.strainFamily || '—';
    document.getElementById('pdg-info-eyecolor').textContent = p.eyeColor || '—';
    const isOwnPigeon = p.orgId === currentOrg.slug;
    const ownerOrg = isOwnPigeon ? currentOrg : orgsBySlug[p.orgId];
    document.getElementById('pdg-info-owner').textContent = ownerOrg ? ownerOrg.name : '—';
    document.getElementById('pdg-edit-btn').onclick = () => openEditModal(id, p);
  }
  function renderPedigreePerformanceSummary(id) {
    const stats = pigeonPerformanceStats(id);
    const wrap = document.getElementById('pdg-perf-wrap');
    const empty = document.getElementById('pdg-perf-empty');
    if (!stats.participated) { wrap.style.display = 'none'; empty.style.display = 'block'; return; }
    wrap.style.display = '';
    empty.style.display = 'none';
    document.getElementById('pdg-perf-races').textContent = stats.participated;
    document.getElementById('pdg-perf-firsts').textContent = stats.firsts;
    document.getElementById('pdg-perf-top10').textContent = stats.top10;
    document.getElementById('pdg-perf-speed').textContent = stats.avgSpeedMPerMin !== null ? Math.round(stats.avgSpeedMPerMin) + ' m/min' : '—';
  }

  // ---- Offspring / Siblings / Race Results / Training / Notes tabs.
  function renderPigeonRelationTable(docs, tbodyId, mobileWrapId, emptyId, showActions) {
    const tbody = document.getElementById(tbodyId);
    const mobileWrap = document.getElementById(mobileWrapId);
    const emptyEl = document.getElementById(emptyId);
    if (emptyEl) emptyEl.style.display = docs.length ? 'none' : 'block';
    const tableWrap = tbody ? tbody.closest('.pdg-rel-table-wrap') : null;
    if (tableWrap) tableWrap.style.display = docs.length ? '' : 'none';
    if (mobileWrap) mobileWrap.style.display = docs.length ? '' : 'none';
    if (tbody) {
      tbody.innerHTML = docs.map(d => `
        <tr>
          <td>${d.p.ringNumber || '—'}</td>
          <td>${d.p.name}</td>
          <td>${d.p.sex === 'cock' ? 'Cock' : d.p.sex === 'hen' ? 'Hen' : '—'}</td>
          <td>${d.p.color || '—'}</td>
          <td>${d.p.dateOfBirth ? fmtDateOnly(new Date(d.p.dateOfBirth + 'T00:00:00').getTime()) : '—'}</td>
          <td><span class="pgn-badge ${d.p.isArchived ? 'pgn-badge--inactive' : 'pgn-badge--active'}">${d.p.isArchived ? 'Inactive' : 'Active'}</span></td>
          ${showActions ? `<td class="pdg-rel-actions">
            <button type="button" class="pdg-rel-act" data-act="view" data-id="${d.id}" title="View Details"><i class="fa-solid fa-eye"></i></button>
            <button type="button" class="pdg-rel-act" data-act="edit" data-id="${d.id}" title="Edit"><i class="fa-solid fa-pen"></i></button>
          </td>` : ''}
        </tr>`).join('');
      tbody.querySelectorAll('.pdg-rel-act').forEach(btn => {
        btn.onclick = () => {
          const doc = findPigeonDoc(btn.dataset.id);
          if (!doc) return;
          if (btn.dataset.act === 'view') {
            pedigreeSubject = { id: doc.id, p: doc.p };
            renderPigeonDetailsPage();
            const c = document.querySelector('.db-content'); if (c) c.scrollTop = 0;
          } else {
            openEditModal(doc.id, doc.p);
          }
        };
      });
    }
    if (mobileWrap) {
      mobileWrap.innerHTML = docs.map(d => `
        <div class="pdg-rel-card" data-pigeon-id="${d.id}">
          <div class="pdg-rel-card-photo">${pigeonAvatarHtml(d.p)}</div>
          <div class="pdg-rel-card-body">
            <div class="pdg-rel-card-name">${d.p.name}</div>
            <div class="pdg-rel-card-meta">${d.p.ringNumber || 'No ring'} · ${d.p.sex === 'cock' ? 'Cock' : d.p.sex === 'hen' ? 'Hen' : 'Unknown'}</div>
          </div>
          <span class="pgn-badge ${d.p.isArchived ? 'pgn-badge--inactive' : 'pgn-badge--active'}">${d.p.isArchived ? 'Inactive' : 'Active'}</span>
        </div>`).join('');
      wirePedigreeClickableBoxes(mobileWrap);
    }
  }
  function renderPedigreeOffspringTab(id, p) {
    const docs = pigeonOffspringOf(id);
    renderPigeonRelationTable(docs, 'pdg-offspring-body', 'pdg-offspring-mobile', 'pdg-offspring-empty', true);
    document.getElementById('pdg-offspring-count').textContent = docs.length;
    document.getElementById('pdg-add-offspring-btn').onclick = () => {
      openAddModal();
      if (p.sex === 'cock') document.getElementById('db-pigeon-sire').value = id;
      else if (p.sex === 'hen') document.getElementById('db-pigeon-dam').value = id;
      setParentsCardOpen(true);
    };
  }
  function renderPedigreeSiblingsTab(id, p) {
    const docs = pigeonSiblingsOf(id, p);
    renderPigeonRelationTable(docs, 'pdg-siblings-body', 'pdg-siblings-mobile', 'pdg-siblings-empty', false);
    document.getElementById('pdg-siblings-count').textContent = docs.length;
  }
  function renderPedigreeTrainingTab(id) {
    const rows = pigeonTrainingRowsOf(id);
    const tableWrap = document.getElementById('pdg-training-table-wrap');
    const emptyEl = document.getElementById('pdg-training-empty');
    if (!rows.length) { tableWrap.style.display = 'none'; emptyEl.style.display = 'block'; return; }
    tableWrap.style.display = '';
    emptyEl.style.display = 'none';
    document.getElementById('pdg-training-body').innerHTML = rows.map(t => {
      const status = trainingStatusOf(t);
      const done = status === 'completed';
      const a = (t.arrivals || {})[id];
      const rank = done ? pigeonRankInTraining(t, id) : null;
      return `<tr>
        <td>${t.releaseAtMs ? fmtDateOnly(t.releaseAtMs) : '—'}</td>
        <td>${trainingNameOf(t)}</td>
        <td>${t.distanceKm || 0} km</td>
        <td><span class="pgn-badge ${done ? 'pgn-badge--active' : 'pgn-badge--inactive'}">${done ? 'Completed' : 'Upcoming'}</span></td>
        <td>${!done ? '—' : a ? Math.round(speedToMPerMin(a.speedKmH)) + ' m/min' : 'Did not return'}</td>
        <td>${rank ? '#' + rank : '—'}</td>
      </tr>`;
    }).join('');
  }
  function renderPedigreeNotesTab(p) {
    const textEl = document.getElementById('pdg-notes-text');
    const emptyEl = document.getElementById('pdg-notes-empty');
    if (p.pedigreeText) { textEl.style.display = 'block'; textEl.textContent = p.pedigreeText; emptyEl.style.display = 'none'; }
    else { textEl.style.display = 'none'; emptyEl.style.display = 'block'; }
  }
  function renderPedigreeSubTabs(id, p) {
    document.querySelectorAll('.pdg-subtab-btn').forEach(b => b.classList.toggle('active', b.dataset.subtab === pedigreeActiveSubTab));
    document.querySelectorAll('.pdg-subtab-pane').forEach(el => el.classList.toggle('active', el.id === 'pdg-subtab-' + pedigreeActiveSubTab));
    switch (pedigreeActiveSubTab) {
      case 'offspring': renderPedigreeOffspringTab(id, p); break;
      case 'siblings': renderPedigreeSiblingsTab(id, p); break;
      case 'training': renderPedigreeTrainingTab(id); break;
      case 'notes': renderPedigreeNotesTab(p); break;
    }
  }

  // ---- Page entry point + main render dispatcher.
  function openPigeonPedigreePage(id, p, fromTab) {
    pedigreeSubject = { id, p };
    pedigreeGenerations = 4;
    pedigreeActiveSubTab = 'offspring';
    pedigreeEntryTab = fromTab || 'pigeon-list';
    activateTab('pigeon-pedigree');
    // Export PDF needs jsPDF (~90KB), which otherwise only starts
    // downloading the moment someone clicks the button -- kicking it off
    // now means by the time anyone actually reaches for Export PDF a few
    // seconds later, loadScriptOnce just hands back the same
    // already-settled promise instead of starting a fresh fetch.
    loadScriptOnce('https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js').catch(() => {});
    pdgLoadLogo();
  }
  function renderPedigreePage() {
    if (!pedigreeSubject) return;
    const { id, p } = pedigreeSubject;
    const maxGen = pedigreeGenerations - 1;

    document.getElementById('pdg-title-ring').textContent = p.ringNumber || 'No ring number';
    document.getElementById('pdg-title-name').textContent = p.name;
    document.getElementById('pdg-gen-select').value = String(pedigreeGenerations);
    {
      const desktopEl = document.getElementById('pdg-tree-desktop');
      const mobileEl = document.getElementById('pdg-tree-mobile');
      desktopEl.innerHTML = renderPedigreeCertHtml(id, p, pedigreeGenerations);
      mobileEl.innerHTML = renderPedigreeMobileHtml(id, p, maxGen);
      wirePedigreeClickableBoxes(desktopEl);
      wirePedigreeClickableBoxes(mobileEl);
      wirePedigreeExpandToggles(mobileEl);
      const scrollWrap = document.getElementById('pdg-tree-scroll');
      if (scrollWrap) {
        scrollWrap.scrollLeft = 0;
      }
    }

  }

  // ---- Pigeon Details page -- Pigeon Information, Performance Summary and the
  // Offspring / Siblings / Race Results / Training / Notes tabs. Same
  // pedigreeSubject state as the pedigree page, but no pedigree chart here.
  function openPigeonDetailsPage(id, p, fromTab) {
    pedigreeSubject = { id, p };
    pedigreeActiveSubTab = 'offspring';
    pedigreeEntryTab = fromTab || 'pigeon-list';
    activateTab('pigeon-details');
  }
  function renderPigeonDetailsPage() {
    if (!pedigreeSubject) return;
    const { id, p } = pedigreeSubject;
    document.getElementById('pgd-title-ring').textContent = p.ringNumber || 'No ring number';
    document.getElementById('pgd-title-name').textContent = p.name;
    renderPedigreeInfoCard(id, p);
    renderPedigreePerformanceSummary(id);
    renderPedigreeSubTabs(id, p);
  }

  // Export PDF draws the report directly with jsPDF's own vector primitives
  // (rects, text, lines) instead of rasterizing HTML via html2canvas: that
  // library clones the ENTIRE page document to render accurately, which on
  // a real storefront means recloning every checkout/cart/payment script
  // Shopify loads alongside it -- that, not the report itself, was the long
  // delay. Drawing directly never touches the surrounding page at all, so it
  // stays fast no matter how much else is loaded.
  const PDF_PAD = 28;
  const PDF_BLUE = [47, 111, 237], PDF_PINK = [214, 51, 108], PDF_INK = [17, 17, 17];
  const PDF_LOGO_URL = 'https://cdn.shopify.com/s/files/1/0920/3443/3405/files/sidebar_logo.png?v=1790401655';
  function pdfBezierTo(pdf, x1, y1, cx1, cy1, cx2, cy2, x2, y2) {
    let px = x1, py = y1;
    for (let i = 1; i <= 16; i++) {
      const t = i / 16, mt = 1 - t;
      const x = mt * mt * mt * x1 + 3 * mt * mt * t * cx1 + 3 * mt * t * t * cx2 + t * t * t * x2;
      const y = mt * mt * mt * y1 + 3 * mt * mt * t * cy1 + 3 * mt * t * t * cy2 + t * t * t * y2;
      pdf.line(px, py, x, y);
      px = x; py = y;
    }
  }
  function pdfInitialsOf(p) {
    return ((p && p.name) || '?').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';
  }
  function pdfAvatar(pdf, cx, cy, r, p) {
    pdf.setFillColor(PDF_INK[0], PDF_INK[1], PDF_INK[2]);
    pdf.circle(cx, cy, r, 'F');
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(r); pdf.setTextColor(255, 255, 255);
    pdf.text(pdfInitialsOf(p), cx, cy + r * 0.35, { align: 'center' });
  }
  // The real male/female signs aren't in jsPDF's built-in fonts (they came
  // out as empty boxes), so they're drawn as vector shapes instead.
  function pdfSexSymbol(pdf, cx, cy, r, sex) {
    const c = sex === 'cock' ? PDF_BLUE : PDF_PINK;
    pdf.setDrawColor(c[0], c[1], c[2]);
    pdf.setLineWidth(Math.max(0.8, r * 0.3));
    if (sex === 'cock') {
      const a = r * 0.55, ox = cx - r * 0.2, oy = cy + r * 0.2;
      pdf.circle(ox, oy, a, 'S');
      const sx = ox + a * 0.707, sy = oy - a * 0.707, tx = cx + r * 0.85, ty = cy - r * 0.85;
      pdf.line(sx, sy, tx, ty);
      pdf.line(tx, ty, tx - r * 0.55, ty);
      pdf.line(tx, ty, tx, ty + r * 0.55);
    } else {
      const a = r * 0.5, oy = cy - r * 0.35;
      pdf.circle(cx, oy, a, 'S');
      pdf.line(cx, oy + a, cx, cy + r * 0.95);
      pdf.line(cx - r * 0.4, cy + r * 0.55, cx + r * 0.4, cy + r * 0.55);
    }
  }
  // Sex shown as a small tinted rectangle: symbol (+ label when there's room).
  function pdfSexRect(pdf, x, y, sex, label, h) {
    const known = sex === 'cock' || sex === 'hen';
    const tint = sex === 'cock' ? [227, 239, 254] : sex === 'hen' ? [253, 228, 238] : [240, 242, 244];
    const col = sex === 'cock' ? PDF_BLUE : sex === 'hen' ? PDF_PINK : [93, 107, 120];
    const r = h * 0.3;
    let w = 0;
    if (label) { pdf.setFont('helvetica', 'bold'); pdf.setFontSize(h * 0.58); w = pdf.getTextWidth(label); }
    const total = (known ? r * 2 + 5 : 0) + w + (label ? 14 : 12);
    pdf.setFillColor(tint[0], tint[1], tint[2]);
    pdf.roundedRect(x, y, total, h, 3, 3, 'F');
    let cx = x + (label ? 7 : total / 2 - r);
    if (known) { pdfSexSymbol(pdf, cx + r, y + h / 2, r, sex); cx += r * 2 + 5; }
    if (label) { pdf.setTextColor(col[0], col[1], col[2]); pdf.text(label, cx, y + h / 2 + h * 0.2); }
    return total;
  }
  // "i" in a blue disc -- the Pigeon Information title icon.
  function pdfInfoIcon(pdf, cx, cy, r) {
    pdf.setFillColor(PDF_BLUE[0], PDF_BLUE[1], PDF_BLUE[2]);
    pdf.circle(cx, cy, r, 'F');
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(r * 1.5); pdf.setTextColor(255, 255, 255);
    pdf.text('i', cx, cy + r * 0.52, { align: 'center' });
  }
  // Absolute-coordinate bezier path -> jsPDF's relative `lines` format. Only
  // the vector bird fallback below uses it.
  function pdfPath(pdf, x0, y0, segs, color) {
    let cx = x0, cy = y0;
    const rel = segs.map(s => {
      const r = s.map((v, i) => v - (i % 2 === 0 ? cx : cy));
      cx = s[s.length - 2]; cy = s[s.length - 1];
      return r;
    });
    pdf.setFillColor(color[0], color[1], color[2]);
    pdf.lines(rel, x0, y0, [1, 1], 'F', true);
  }
  // Vector stand-in for the logo, used only if the real logo image can't be
  // fetched in time.
  function pdfBird(pdf, x, y) {
    pdfPath(pdf, x + 1, y + 21, [[x + 1, y + 11, x + 9, y + 3, x + 22, y + 1], [x + 17, y + 6, x + 17, y + 11, x + 20, y + 15], [x + 14, y + 13, x + 7, y + 15, x + 1, y + 21]], [29, 78, 184]);
    pdfPath(pdf, x + 15, y + 17, [[x + 17, y + 9, x + 25, y + 3, x + 33, y + 4], [x + 29, y + 8, x + 29, y + 12, x + 31, y + 16], [x + 26, y + 14, x + 20, y + 16, x + 15, y + 17]], PDF_BLUE);
    pdfPath(pdf, x + 11, y + 23, [[x + 14, y + 19, x + 19, y + 18, x + 23, y + 20], [x + 20, y + 24, x + 15, y + 27, x + 11, y + 23]], [96, 165, 250]);
  }
  function pdfBadge(pdf, x, y, text, fill, color) {
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
    const w = pdf.getTextWidth(text) + 16;
    pdf.setFillColor(fill[0], fill[1], fill[2]);
    pdf.roundedRect(x, y, w, 16, 8, 8, 'F');
    pdf.setTextColor(color[0], color[1], color[2]);
    pdf.text(text, x + w / 2, y + 11, { align: 'center' });
    return w;
  }
  // The brand logo is one small, same-CDN image (the only image in the
  // whole export), fetched once in the background when the Pedigree page
  // opens and re-encoded as a compact JPEG. If it isn't ready in time the
  // export just falls back to the vector mark instead of waiting on it.
  let pdgLogoPromise = null;
  // PDF logo: transparent PNG (navy text on no background, so it sits cleanly on white paper), built into the page so there is nothing to download and it can never be late.
  const PDF_LOGO_PNG = 'data:image/png;base64,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';
  function pdgLoadLogo() {
    if (!pdgLogoPromise) pdgLogoPromise = Promise.resolve({ data: PDF_LOGO_PNG, ratio: 726 / 160 });
    return pdgLogoPromise;
  }
  function drawPedigreeTreeOnPdf(pdf, layout, treeTop) {
    const { positions, connectors, labels, boxW, boxH, padding, labelRowH } = layout;
    const topOffset = padding + labelRowH;
    connectors.forEach(c => {
      const a = positions[c.from], b = positions[c.to];
      if (!a || !b) return;
      const fromLeft = a.x < b.x;
      const ax = PDF_PAD + (fromLeft ? a.x + boxW : a.x), ay = treeTop + a.y + boxH / 2 + topOffset;
      const bx = PDF_PAD + (fromLeft ? b.x : b.x + boxW), by = treeTop + b.y + boxH / 2 + topOffset;
      const midX = (ax + bx) / 2;
      const color = c.side === 'sire' ? PDF_BLUE : PDF_PINK;
      pdf.setDrawColor(color[0], color[1], color[2]);
      pdf.setLineWidth(1.1);
      pdfBezierTo(pdf, ax, ay, midX, ay, midX, by, bx, by);
    });
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(7.5); pdf.setTextColor(138, 151, 163);
    labels.forEach(l => {
      const cx = PDF_PAD + l.x + boxW / 2;
      pdf.text((l.gen === 0 ? 'CURRENT PIGEON' : 'GENERATION ' + l.gen), cx, treeTop + 10, { align: 'center' });
      if (l.gen > 0) {
        pdf.setFont('helvetica', 'normal');
        pdf.text(l.text, cx, treeTop + 21, { align: 'center' });
        pdf.setFont('helvetica', 'bold');
      }
    });
    Object.values(positions).forEach(pos => {
      const x = PDF_PAD + pos.x, y = treeTop + pos.y + topOffset;
      if (!pos.doc) {
        pdf.setDrawColor(215, 221, 226); pdf.setFillColor(250, 250, 250);
        pdf.roundedRect(x, y, boxW, boxH, 6, 6, 'FD');
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9); pdf.setTextColor(138, 151, 163);
        pdf.text('Not recorded', x + boxW / 2, y + boxH / 2 + 3, { align: 'center' });
        return;
      }
      const tint = pos.side === 'self' ? [234, 243, 255] : pos.side === 'sire' ? [243, 247, 255] : pos.side === 'dam' ? [253, 238, 243] : [255, 255, 255];
      pdf.setFillColor(tint[0], tint[1], tint[2]);
      const border = pos.side === 'self' ? PDF_BLUE : [215, 221, 226];
      pdf.setDrawColor(border[0], border[1], border[2]);
      pdf.setLineWidth(pos.side === 'self' ? 1.3 : 0.75);
      pdf.roundedRect(x, y, boxW, boxH, 6, 6, 'FD');
      pdfAvatar(pdf, x + 18, y + boxH / 2, 11, pos.doc.p);
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9.5); pdf.setTextColor(0, 0, 0);
      pdf.text(pos.doc.p.name, x + 34, y + boxH / 2 - 3, { maxWidth: boxW - 40 });
      let mx = x + 34;
      const sex = pos.doc.p.sex;
      if (sex === 'cock' || sex === 'hen') mx += pdfSexRect(pdf, mx, y + boxH / 2 + 3, sex, '', 12) + 4;
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); pdf.setTextColor(0, 0, 0);
      pdf.text(pos.doc.p.ringNumber || '', mx, y + boxH / 2 + 12, { maxWidth: boxW - (mx - x) - 6 });
    });
  }
  // ---- Certificate-style pedigree on the PDF: the SAME geometry as the screen
  // chart (pdgCertGeometry), drawn like the printed reference -- white boxes,
  // 9pt typewriter text, solid black drop-shadow, red ring numbers. Each box
  // shows only the lines that fit its height; longer text ends with "..."
  // (the full text stays saved on the pigeon).
  function buildCertPdfLayout(mpdf, subjectId, subjectP, totalGens) {
    const G = PDG_G;
    const textW = G.W - G.PADX * 2;
    totalGens = pdgEffectiveGens(subjectId, subjectP, totalGens);
    const nodes = pdgCertNodes(subjectId, subjectP, totalGens);
    const gm = pdgCertGeometry(totalGens);
    mpdf.setFont('courier', 'normal'); mpdf.setFontSize(9);
    nodes.forEach(n => {
      n.lines = [];
      if (n.doc) {
        n.f = pdgCertFacts(n.doc, n.gen, n.isSire);
        [n.f.name].concat(n.f.facts, [n.f.notes]).filter(Boolean).forEach(t => {
          mpdf.splitTextToSize(t, textW).forEach(l => n.lines.push(l));
        });
      }
      Object.assign(n, gm.geo[n.gen + ':' + n.idx]);
    });
    return { nodes, lines: gm.lines, chartW: gm.width, chartH: gm.height, totalGens };
  }
  function pdfCertEllipsis(pdf, text, maxW) {
    let t = text.replace(/\s+$/, '');
    while (t.length > 1 && pdf.getTextWidth(t + '...') > maxW) t = t.slice(0, -1);
    return t + '...';
  }
  function drawCertPdf(pdf, cert, left, top) {
    const G = PDG_G;
    const textW = G.W - G.PADX * 2;
    pdf.setDrawColor(140, 140, 140); pdf.setLineWidth(0.7);
    cert.lines.forEach(l => pdf.line(left + l[0], top + l[1], left + l[2], top + l[3]));
    cert.nodes.forEach(n => {
      const x = left + n.x, y = top + n.y, h = n.h, W = G.W;
      pdf.setFillColor(0, 0, 0);
      pdf.roundedRect(x + G.SHADOW, y + G.SHADOW, W, h, 2.5, 2.5, 'F');
      pdf.setFillColor(255, 255, 255); pdf.setDrawColor(30, 30, 30); pdf.setLineWidth(0.5);
      pdf.roundedRect(x, y, W, h, 2.5, 2.5, 'FD');
      if (!n.doc) return;
      let ty = y + G.PADY + 7.5;
      pdf.setFont('courier', 'bold'); pdf.setFontSize(9); pdf.setTextColor(210, 15, 25);
      pdf.text(n.f.ring + (n.f.sex ? ' ' + n.f.sex : ''), x + G.PADX, ty, { maxWidth: textW });
      ty += G.LINE;
      const avail = pdgBoxLines(h);
      let lines = n.lines;
      if (lines.length > avail) {
        lines = lines.slice(0, avail);
        if (lines.length) {
          pdf.setFont('courier', 'normal'); pdf.setFontSize(9);
          lines = lines.slice(0, -1).concat([pdfCertEllipsis(pdf, lines[lines.length - 1], textW)]);
        }
      }
      pdf.setFont('courier', 'normal'); pdf.setFontSize(9); pdf.setTextColor(0, 0, 0);
      lines.forEach(l => { pdf.text(l, x + G.PADX, ty); ty += G.LINE; });
    });
    pdf.setTextColor(0, 0, 0);
  }
  // The permanent public page for a pigeon's pedigree (template page.pedigree.liquid on the
  // storefront). The same address is printed as a QR code on every pedigree PDF.
  const PEDIGREE_PUBLIC_URL = 'https://www.flightpathlive.com/pages/pedigree';
  function pedigreePublicLink(pigeonId) { return PEDIGREE_PUBLIC_URL + '?p=' + encodeURIComponent(pigeonId); }
  const QR_LIB_URL = 'https://cdnjs.cloudflare.com/ajax/libs/qrcode-generator/1.4.4/qrcode.min.js';
  function pdfDrawQr(pdf, text, x, y, size) {
    if (typeof qrcode !== 'function') return false;
    try {
      const qr = qrcode(0, 'M');
      qr.addData(text); qr.make();
      const n = qr.getModuleCount(), cell = size / n;
      pdf.setFillColor(0, 0, 0);
      for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) {
        if (qr.isDark(r, c)) pdf.rect(x + c * cell, y + r * cell, cell + 0.15, cell + 0.15, 'F');
      }
      return true;
    } catch (e) { return false; }
  }
  function exportPedigreePdf() {
    if (!pedigreeSubject) return;
    const { id, p } = pedigreeSubject;
    const isOwnPigeon = p.orgId === currentOrg.slug;
    const ownerOrg = isOwnPigeon ? currentOrg : orgsBySlug[p.orgId];
    const userName = (document.getElementById('db-avatar-name') || {}).textContent || 'User';
    const userEmail = (auth.currentUser && auth.currentUser.email) || '';
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
      ', ' + now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

    const TITLE_H = 74, TREE_GAP = 22, FOOTER_H = 76, QR_SIZE = 58;

    Promise.all([
      loadScriptOnce('https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js'),
      Promise.race([pdgLoadLogo(), new Promise(r => setTimeout(() => r(null), 1500))]),
      loadScriptOnce(QR_LIB_URL).catch(() => null)
    ]).then(([, logo]) => {
      const { jsPDF } = window.jspdf;
      const cert = buildCertPdfLayout(new jsPDF({ unit: 'pt' }), id, p, pedigreeGenerations);
      const pageW = Math.max(cert.chartW + PDF_PAD * 2, 640);
      const pageH = PDF_PAD + TITLE_H + cert.chartH + TREE_GAP + FOOTER_H + PDF_PAD;
      const pdf = new jsPDF({ orientation: pageW >= pageH ? 'l' : 'p', unit: 'pt', format: [pageW, pageH] });
      let y = PDF_PAD;

      // Logo, top left.
      let logoW = 0;
      if (logo) {
        const lh = 26.6; logoW = lh * logo.ratio; // 30% smaller than before (was 38)
        pdf.addImage(logo.data, 'PNG', PDF_PAD, y, logoW, lh);
      } else {
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(15);
        const wA = pdf.getTextWidth('FlightPath'), wB = pdf.getTextWidth('Live');
        pdfBird(pdf, PDF_PAD, y);
        pdf.setTextColor(14, 41, 69); pdf.text('FlightPath', PDF_PAD + 42, y + 18);
        pdf.setTextColor(47, 111, 237); pdf.text('Live', PDF_PAD + 42 + wA, y + 18);
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7.5); pdf.setTextColor(93, 118, 146);
        pdf.text('Loft Management System', PDF_PAD + 42, y + 29);
        logoW = 42 + wA + wB;
      }
      // Title, typewriter style like the printed pedigree, centred in the space right of the logo.
      const titleLeft = PDF_PAD + logoW + 14, titleRight = pageW - PDF_PAD - QR_SIZE - 12, titleCx = (titleLeft + titleRight) / 2;
      pdf.setFont('courier', 'bold'); pdf.setFontSize(15); pdf.setTextColor(0, 0, 0);
      pdf.text('Pedigree: ' + (p.ringNumber || 'No ring number') + (p.sex === 'cock' ? ' M' : p.sex === 'hen' ? ' F' : ''), titleCx, y + 19, { align: 'center', maxWidth: titleRight - titleLeft });
      // QR code, top right: scanning it opens this pigeon's permanent public pedigree page.
      pdfDrawQr(pdf, pedigreePublicLink(id), pageW - PDF_PAD - QR_SIZE, y - 2, QR_SIZE);
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7.5); pdf.setTextColor(93, 118, 146);
      pdf.text('Scan to open', pageW - PDF_PAD - QR_SIZE / 2, y - 2 + QR_SIZE + 9, { align: 'center' });
      pdf.setTextColor(0, 0, 0);
      y += TITLE_H;
      drawCertPdf(pdf, cert, PDF_PAD, y);
      y += cert.chartH + TREE_GAP;

      // Footer: page number (left), account name + email (right).
      pdf.setDrawColor(220, 231, 240); pdf.setLineWidth(1); pdf.line(PDF_PAD, y, pageW - PDF_PAD, y);
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(11); pdf.setTextColor(0, 0, 0);
      pdf.text('Page 1 of 1', PDF_PAD, y + 42);
      const rx = pageW - PDF_PAD;
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(13);
      const nameW = pdf.getTextWidth(userName);
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10.5);
      const mailW = userEmail ? pdf.getTextWidth(userEmail) : 0;
      pdf.setFontSize(8.5);
      const blockW = Math.max(nameW, mailW, pdf.getTextWidth('GENERATED BY'));
      pdfAvatar(pdf, rx - blockW - 28, y + 38, 18, { name: userName });
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); pdf.setTextColor(93, 118, 146);
      pdf.text('GENERATED BY', rx, y + 24, { align: 'right' });
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(13); pdf.setTextColor(0, 0, 0);
      pdf.text(userName, rx, y + 40, { align: 'right' });
      if (userEmail) {
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10.5); pdf.setTextColor(0, 0, 0);
        pdf.text(userEmail, rx, y + 54, { align: 'right' });
      }

      pdf.save('pedigree-' + (p.ringNumber || p.name).replace(/[^a-z0-9]+/gi, '-') + '.pdf');
    }).catch(() => showToast('Could not generate the PDF -- please check your connection and try again.'));
  }

  document.getElementById('pgd-back-btn').onclick = () => activateTab(pedigreeEntryTab);
  document.getElementById('pgd-breadcrumb-pigeons').onclick = () => activateTab(pedigreeEntryTab);
  document.getElementById('pdg-back-btn').onclick = () => activateTab(pedigreeEntryTab);
  document.getElementById('pdg-breadcrumb-pigeons').onclick = () => activateTab(pedigreeEntryTab);
  document.getElementById('pdg-gen-select').onchange = (e) => { pedigreeGenerations = parseInt(e.target.value, 10); renderPedigreePage(); };
  document.getElementById('pdg-export-pdf-btn').onclick = exportPedigreePdf;
  document.getElementById('pdg-copy-link-btn').onclick = async () => {
    if (!pedigreeSubject) return;
    const link = pedigreePublicLink(pedigreeSubject.id);
    try { await navigator.clipboard.writeText(link); showToast('✅ Pedigree link copied'); }
    catch (e) { window.prompt('Copy this pedigree link:', link); }
  };
  document.querySelectorAll('.pdg-subtab-btn').forEach(btn => {
    btn.onclick = () => { pedigreeActiveSubTab = btn.dataset.subtab; renderPedigreeSubTabs(pedigreeSubject.id, pedigreeSubject.p); };
  });

  // Duplicates a pigeon's basic info into a new record -- deliberately NOT
  // the ring number (has to stay unique per real bird) or assignedTrackerId
  // (a physical tracker can't be on two birds at once); everything else
  // carries over as a starting point to edit from.
  async function copyPigeon(p) {
    try {
      await db.collection('pigeons').add({
        name: p.name + ' (copy)', ringNumber: '', color: p.color || '', photoURL: p.photoURL || null,
        markerColor: p.markerColor || pickAutoMarkerColor(), dateOfBirth: p.dateOfBirth || null,
        sex: p.sex || 'unknown', eyeColor: p.eyeColor || '', strainFamily: p.strainFamily || '',
        shortDescription: p.shortDescription || '', pedigreeText: p.pedigreeText || '',
        sireId: p.sireId || null, damId: p.damId || null,
        orgId: currentOrg.slug, ownerUid: auth.currentUser.uid,
        createdAt: firebase.firestore.FieldValue.serverTimestamp()
      });
      showToast('✅ Copied "' + p.name + '"');
    } catch (e) {
      showToast('Could not copy: ' + e.message);
    }
  }

